// FeedWizardTab.tsx — v6 Canvas Editor
import { useState, useEffect, useCallback, useRef, MouseEvent } from 'react';
import { T, MONO, radius, shadow, tint } from './poyais-ui/theme';
import { Card, SectionHeader, Badge, Button, Progress, Grid, Stat } from './poyais-ui/primitives';
import { useLang, addTranslations } from './i18n';
import PoyaisSpinner from './PoyaisSpinner';

addTranslations({
  // Panel labels / sidebar
  'Ürünler': 'Products',
  'Şablonlar': 'Templates',
  'Yazı': 'Text',
  'Fotoğraflar': 'Photos',
  'Şekiller': 'Shapes',
  'Görseller': 'Images',
  'Arka Plan': 'Background',
  'Katmanlar': 'Layers',
  'Ölçeklendir': 'Resize',
  // Feed field hints
  'Ana ürün görseli': 'Main product image',
  'Ek görsel 1': 'Additional image 1',
  'Ek görsel 2': 'Additional image 2',
  'Ek görsel 3': 'Additional image 3',
  'Ek görsel 4': 'Additional image 4',
  'Arka plan kaldırılmış': 'Background removed',
  'Ürün başlığı': 'Product title',
  'Ürün kodu': 'Product code',
  'Normal fiyat': 'Regular price',
  'İndirimli fiyat': 'Sale price',
  // Shape presets
  'Dikdörtgen': 'Rectangle',
  'Yuvarlak Kutu': 'Rounded Box',
  'Kare': 'Square',
  'Yuvarlak Kare': 'Rounded Square',
  'Daire': 'Circle',
  'Yatay Çizgi': 'Horizontal Line',
  'Kalın Çizgi': 'Thick Line',
  'İnce Çizgi': 'Thin Line',
  'Rozet Oval': 'Oval Badge',
  'Rozet Kare': 'Square Badge',
  'Rozet Pill': 'Pill Badge',
  'Koyu Overlay': 'Dark Overlay',
  'Altın Overlay': 'Gold Overlay',
  'Beyaz Overlay': 'White Overlay',
  // Status badge
  'Aktif': 'Active',
  'Taslak': 'Draft',
  'Üretiliyor': 'Generating',
  // FeedPanel
  'Boş alanlı ürünler bu tasarıma dahil edilmez.': 'Products with empty fields are not included in this design.',
  '%91 ürün dahil edildi.': '91% of products included.',
  'Referans Alanları': 'Reference Fields',
  'Tasarımınızda kullanabileceğiniz katalog elementleri.': 'Catalog elements you can use in your design.',
  'Önce Ürünler panelinden bir ürün seçin.': 'First select a product from the Products panel.',
  // ProductsPanel
  'Yükleniyor...': 'Loading...',
  'ürün': 'products',
  '← Önceki': '← Previous',
  'Sonraki →': 'Next →',
  // LayerPropsPanel
  "Canvas'ta bir öğe seçin veya Feed panelinden alan ekleyin.": 'Select an item on the canvas or add a field from the Feed panel.',
  'Görsel katmanı': 'Image layer',
  'Metin katmanı': 'Text layer',
  'Sil': 'Delete',
  'Konum & Boyut': 'Position & Size',
  'Genişlik': 'Width',
  'Yükseklik': 'Height',
  'Metin': 'Text',
  'Font Boyutu': 'Font Size',
  'Font Ağırlık': 'Font Weight',
  'Metin Rengi': 'Text Color',
  'Hizalama': 'Alignment',
  'Font Tipi': 'Font Family',
  'Varsayılan': 'Default',
  'İç Boşluk': 'Padding',
  'Yuvarlak Köşe': 'Border Radius',
  'Şekil Rengi': 'Shape Color',
  'Opaklık': 'Opacity',
  'Köşe Yuvarlama': 'Corner Radius',
  'Görsel Seçenekleri': 'Image Options',
  'Nesne Sığdırma': 'Object Fit',
  'Cover (kırp)': 'Cover (crop)',
  'Contain (tam sığdır)': 'Contain (fit entirely)',
  'Görsel URL': 'Image URL',
  // ElementsPanel / TextPanel
  'Şekil Ekle': 'Add Shape',
  "Seçince canvas'a ekler, sağdan düzenleyebilirsin.": "Selecting it adds it to the canvas — you can edit it from the right panel.",
  'Hazır Metin Stilleri': 'Preset Text Styles',
  // PhotosPanel
  "Pexels'ta ara...": 'Search on Pexels...',
  'Sonuç bulunamadı': 'No results found',
  // LayersListPanel
  'Henüz katman yok. Feed panelinden alan ekleyin.': 'No layers yet. Add a field from the Feed panel.',
  // TemplatesPanel
  'Ara...': 'Search...',
  // BackgroundPanel
  'Arka Plan Rengi': 'Background Color',
  // AssetsPanel
  '↑ Bilgisayardan Yükle': '↑ Upload from Computer',
  'Ekle': 'Add',
  'Seçili': 'Selected',
  'görsele tıklayınca uygular': 'click an image to apply it',
  'Henüz görsel yok': 'No images yet',
  'Uygula': 'Apply',
  // SaveSuccessModal
  'Tasarım Kaydedildi!': 'Design Saved!',
  "Feed URL'niz hazır. Meta Ads veya Google'da kullanabilirsiniz.": 'Your feed URL is ready. You can use it in Meta Ads or Google.',
  '✓ Kopyalandı': '✓ Copied',
  '📋 URL Kopyala': '📋 Copy URL',
  '🔗 Önizle': '🔗 Preview',
  'Kapat': 'Close',
  // Editor shell
  'Kaydediliyor...': 'Saving...',
  '✓ Kaydedildi': '✓ Saved',
  'Kaydet': 'Save',
  'Temizle': 'Clear',
  '🗑 Sil': '🗑 Delete',
  "Feed panelinden alan ekle veya canvas'ta seç.": 'Add a field from the Feed panel or select one on the canvas.',
  'Özellikler': 'Properties',
  "Canvas'ta bir öğeye tıklayın.": 'Click an item on the canvas.',
  // CreateWizardModal
  'Tasarım Oluştur': 'Create Design',
  'Feed tabanlı dinamik görsel üreticisi': 'Feed-based dynamic image generator',
  'Şablon Seç': 'Select Template',
  'İleri →': 'Next →',
  'Katalog': 'Catalog',
  'Tasarım Adı': 'Design Name',
  'Tasarım adını girin...': 'Enter design name...',
  '← Geri': '← Back',
  'Oluşturuluyor...': 'Creating...',
  'Editöre Geç →': 'Go to Editor →',
  // FeedRegistrySettings
  "Bağlı Feed'ler": 'Connected Feeds',
  'Feed Ekle': 'Add Feed',
  'Ad ve URL zorunludur.': 'Name and URL are required.',
  'Feed adı': 'Feed name',
  'Meta Katalog': 'Meta Catalog',
  'Müşteriye bağlama (isteğe bağlı)': 'Link to a client (optional)',
  'Feed eklenirken hata oluştu.': 'An error occurred while adding the feed.',
  'Ekleniyor...': 'Adding...',
  'İptal': 'Cancel',
  'Henüz bağlı feed yok. Yukarıdan bir feed ekleyin.': 'No connected feeds yet. Add one above.',
  'Feed Adı': 'Feed Name',
  'Bu feed bağlantısını silmek istediğinize emin misiniz? Buna bağlı tasarımlar etkilenebilir.': 'Are you sure you want to delete this feed connection? Designs linked to it may be affected.',
  'Kaydedilirken hata oluştu.': 'An error occurred while saving.',
  'Atanmamış': 'Unassigned',
  'Düzenle': 'Edit',
  // Main tab
  'Katalog tabanlı dinamik feed görselleri oluşturun.': 'Create catalog-based dynamic feed visuals.',
  'Yeni Tasarım': 'New Design',
  'Tasarımlar': 'Designs',
  'Ayarlar': 'Settings',
  'Tasarım oluşturmadan önce Ayarlar sekmesinden bir feed bağlamanız gerekiyor.': 'You need to connect a feed from the Settings tab before creating a design.',
  'Tasarım': 'Design',
  'Toplam Ürün': 'Total Products',
  'Tasarımlarım': 'My Designs',
  'Ürün': 'Product',
  'Durum': 'Status',
  'İşlemler': 'Actions',
  'Henüz hiç tasarım yok': 'No designs yet',
  'İlk dinamik feed tasarımınızı oluşturun': 'Create your first dynamic feed design',
  'Önce Ayarlar sekmesinden bir feed bağlayın.': 'First connect a feed from the Settings tab.',
  'Ayarlar’a Git': 'Go to Settings',
  'Görsel': 'Image',
  'Bu tasarımı silmek istediğinize emin misiniz?': 'Are you sure you want to delete this design?',
});

// Design-kit tokens, aliased to the editor's original local names so every
// call site below (hundreds of references) picks up the shared palette
// without a mechanical per-line rewrite.
const G=T.accent,GL=tint(T.accent,40),S1=T.surface,S2=T.card,S3=T.border,
  BD=T.borderMid,TX=T.text,MT=T.textDim,GR=T.greenText,RD=T.redText,BG=T.bg;
const API='/api/v1/feed-wizard';
const CANVAS_W=900, CANVAS_H=900;

// ── Types ──────────────────────────────────────────────────────────────────────
interface Feed{id:string;name:string;catalog:string;template:string;status:'active'|'draft'|'generating';products:number;cached:number;createdAt:string;src_url?:string;proxy_url?:string;}
interface Product{id:string;title:string;price:string;original_price?:string;brand:string;badge?:string;img:string;additional_images?:string[];link?:string;}

type LayerType='image'|'text'|'shape';
interface Layer{
  id:string; type:LayerType; field:string;
  x:number; y:number; w:number; h:number;
  // image
  url?:string; objectFit?:'cover'|'contain';
  // text
  text?:string; fontSize?:number; fontWeight?:number; color?:string;
  bgColor?:string; padding?:number; textAlign?:'left'|'center'|'right';
  borderRadius?:number;
  fontFamily?:string;
  // shape
  bg?:string;
  opacity?:number;
}

type EditorPanel='feed'|'products'|'templates'|'text'|'photos'|'elements'|'assets'|'background'|'layers'|'resize';
type WizardStep=1|2;

const GOOGLE_FONTS = [
  'inherit','Arial','Georgia','Times New Roman','Verdana','Trebuchet MS','Impact',
  'DM Sans','DM Mono','DM Serif Display',
  'Inter','Roboto','Open Sans','Lato','Montserrat','Poppins','Nunito','Raleway',
  'Oswald','Merriweather','Playfair Display','Source Sans Pro','Ubuntu','Noto Sans',
  'Barlow','Barlow Condensed','Mulish','Quicksand','Josefin Sans','Exo 2',
  'Space Grotesk','Space Mono','Fira Sans','Fira Code','IBM Plex Sans','IBM Plex Mono',
  'Outfit','Syne','Manrope','Plus Jakarta Sans','Be Vietnam Pro',
  'Cormorant Garamond','EB Garamond','Libre Baskerville','Lora','Spectral',
  'Cinzel','Bodoni Moda','Fraunces','Cardo',
  'Pacifico','Dancing Script','Caveat','Satisfy','Lobster','Righteous','Fredoka One',
  'Bebas Neue','Anton','Black Han Sans','Passion One','Boogaloo',
  'Noto Serif','Noto Sans JP','Noto Sans TR',
  'Work Sans','Karla','Jost','Lexend','Red Hat Display',
];

const TEMPLATES=[
  {id:'minimal-dark',name:'Minimal Dark'},{id:'clean-white',name:'Clean White'},
  {id:'gradient-luxury',name:'Luxury Gold'},{id:'bold-promo',name:'Bold Promo'},
  {id:'fashion-dark',name:'Fashion Dark'},{id:'fashion-light',name:'Fashion Light'},
  {id:'tech-blue',name:'Tech Blue'},{id:'food-warm',name:'Food Warm'},
  {id:'seasonal-dark',name:'Seasonal Dark'},{id:'glass-premium',name:'Glass Premium'},
];
const TPL_SWATCH:Record<string,{bg:string;accent:string}> = {
  'minimal-dark':    {bg:'#0f0f0f', accent:'#C9A84C'},
  'clean-white':     {bg:'#f5f5f5', accent:'#111111'},
  'gradient-luxury': {bg:'#2A1F0A', accent:'#C9A84C'},
  'bold-promo':      {bg:'#300000', accent:'#E05252'},
  'fashion-dark':    {bg:'#111111', accent:'#C9A84C'},
  'fashion-light':   {bg:'#fafafa', accent:'#1a1a1a'},
  'tech-blue':       {bg:'#050d1a', accent:'#4d9fff'},
  'food-warm':       {bg:'#1c0f00', accent:'#e8740a'},
  'seasonal-dark':   {bg:'#0d1f0d', accent:'#4caf50'},
  'glass-premium':   {bg:'#1a1a2e', accent:'#a8d8ff'},
};
const FORMATS=[
  {id:'sq',label:'1:1',w:900,h:900,desc:'Feed / Shopping'},
  {id:'port',label:'4:5',w:900,h:1125,desc:'Instagram Feed'},
  {id:'story',label:'9:16',w:900,h:1600,desc:'Stories / Reels'},
  {id:'land',label:'1.91:1',w:1200,h:628,desc:'FB Link / Display'},
];
const PANEL_LABELS:Record<EditorPanel,string>={feed:'Feed',products:'Ürünler',templates:'Şablonlar',text:'Yazı',photos:'Fotoğraflar',elements:'Şekiller',assets:'Görseller',background:'Arka Plan',layers:'Katmanlar',resize:'Ölçeklendir'};
const PANEL_ICONS:Record<EditorPanel,string>={feed:'⬡',products:'🏷',templates:'▦',text:'T',photos:'⬜',elements:'◈',assets:'↑',background:'⊞',layers:'⧉',resize:'⤡'};

// Feed field definitions
const FEED_FIELDS = [
  {key:'image_link',       label:'image_link',            type:'image' as LayerType, hint:'Ana ürün görseli'},
  {key:'additional_image_link.0', label:'additional_image_link.0', type:'image' as LayerType, hint:'Ek görsel 1'},
  {key:'additional_image_link.1', label:'additional_image_link.1', type:'image' as LayerType, hint:'Ek görsel 2'},
  {key:'additional_image_link.2', label:'additional_image_link.2', type:'image' as LayerType, hint:'Ek görsel 3'},
  {key:'additional_image_link.3', label:'additional_image_link.3', type:'image' as LayerType, hint:'Ek görsel 4'},
  {key:'image_link_no_bg', label:'image_link_no_bg',      type:'image' as LayerType, hint:'Arka plan kaldırılmış'},
  {key:'title',            label:'title',                  type:'text'  as LayerType, hint:'Ürün başlığı'},
  {key:'id',               label:'id',                    type:'text'  as LayerType, hint:'Ürün kodu'},
  {key:'price',            label:'price',                 type:'text'  as LayerType, hint:'Normal fiyat'},
  {key:'sale_price',       label:'sale_price',            type:'text'  as LayerType, hint:'İndirimli fiyat'},
];

// ── Primitives ─────────────────────────────────────────────────────────────────
const Label=({children}:any)=><div style={{fontSize:10,fontWeight:700,color:G,letterSpacing:'0.12em',textTransform:'uppercase' as const,marginBottom:6}}>{children}</div>;
const SLabel=({children,style={}}:any)=><div style={{fontSize:10,color:MT,textTransform:'uppercase' as const,letterSpacing:'0.1em',marginBottom:6,...style}}>{children}</div>;
const Inp=({value,onChange,placeholder,style={},type='text'}:any)=>(
  <input type={type} value={value} onChange={(e:any)=>onChange(e.target.value)} placeholder={placeholder}
    style={{width:'100%',boxSizing:'border-box' as const,background:S2,border:`1px solid ${BD}`,borderRadius:6,padding:'7px 10px',color:TX,fontSize:12,outline:'none',...style}}/>
);
const Sel=({value,onChange,options,style={}}:any)=>(
  <select value={value} onChange={(e:any)=>onChange(e.target.value)}
    style={{width:'100%',boxSizing:'border-box' as const,background:S2,border:`1px solid ${BD}`,borderRadius:6,padding:'7px 10px',color:TX,fontSize:12,outline:'none',cursor:'pointer',...style}}>
    {options.map((o:any)=><option key={typeof o==='string'?o:o.value} value={typeof o==='string'?o:o.value}>{typeof o==='string'?o:o.label}</option>)}
  </select>
);
const Btn=({children,onClick,primary=false,small=false,danger=false,disabled=false,style={}}:any)=>(
  <button onClick={onClick} disabled={disabled}
    style={{padding:small?'5px 12px':'9px 20px',borderRadius:6,fontSize:small?11:13,fontWeight:700,cursor:disabled?'default':'pointer',
      background:primary?`linear-gradient(135deg,${G},${GL})`:danger?'rgba(224,82,82,0.1)':S1,
      border:`1px solid ${primary?'transparent':danger?RD:BD}`,
      color:primary?'#1a1200':danger?RD:TX,opacity:disabled?0.45:1,transition:'all 0.15s',whiteSpace:'nowrap' as const,...style}}>
    {children}
  </button>
);
const Toggle=({value,onChange,label}:any)=>(
  <div style={{display:'flex',alignItems:'center',justifyContent:'space-between',marginBottom:8,gap:8}}>
    <span style={{fontSize:12,color:TX,flex:1}}>{label}</span>
    <div onClick={()=>onChange(!value)} style={{width:34,height:18,borderRadius:9,background:value?G:BD,cursor:'pointer',position:'relative' as const,transition:'background 0.2s',flexShrink:0}}>
      <div style={{position:'absolute' as const,top:1,left:value?17:1,width:16,height:16,borderRadius:'50%',background:'#fff',transition:'left 0.2s',boxShadow:'0 1px 3px rgba(0,0,0,0.2)'}}/>
    </div>
  </div>
);


function loadGoogleFont(family:string){
  if(!family||family==='inherit'||document.querySelector(`link[data-font="${family}"]`)) return;
  const link=document.createElement('link');
  link.rel='stylesheet';
  link.setAttribute('data-font',family);
  link.href=`https://fonts.googleapis.com/css2?family=${encodeURIComponent(family)}:wght@400;600;700;800;900&display=swap`;
  document.head.appendChild(link);
}

// ── Helpers ────────────────────────────────────────────────────────────────────
function getFieldValue(field:string, product:Product):string{
  switch(field){
    case 'image_link': return product.img;
    case 'image_link_no_bg': return `${API}/render?src=${encodeURIComponent(product.img)}&tpl=minimal-dark&fmt=sq&remove_bg=true&title=&price=&brand=&badge=&cta_label=NONE&pid=${encodeURIComponent(product.id)}`;
    case 'additional_image_link.0': return product.additional_images?.[0]||'';
    case 'additional_image_link.1': return product.additional_images?.[1]||'';
    case 'additional_image_link.2': return product.additional_images?.[2]||'';
    case 'additional_image_link.3': return product.additional_images?.[3]||'';
    case 'title': return product.title;
    case 'id': return product.id;
    case 'price': return product.original_price||product.price;
    case 'sale_price': return product.price;
    default: return '';
  }
}

function makeDefaultLayer(field:string, type:LayerType, canvasW:number, canvasH:number, existingCount:number):Layer{
  const offset = existingCount * 10;
  if(type==='image'){
    return {id:Date.now().toString()+Math.random(),type:'image',field,x:offset,y:offset,w:Math.round(canvasW*0.5),h:Math.round(canvasH*0.5),url:'',objectFit:'cover'};
  } else {
    const textDefaults:Record<string,Partial<Layer>>={
      title:   {w:Math.round(canvasW*0.7),h:60,fontSize:32,fontWeight:700,color:'#ffffff',bgColor:'',padding:8,textAlign:'left',borderRadius:4},
      price:   {w:200,h:50,fontSize:36,fontWeight:900,color:'#C9A84C',bgColor:'',padding:8,textAlign:'left',borderRadius:4},
      sale_price:{w:200,h:50,fontSize:28,fontWeight:700,color:'#E05252',bgColor:'',padding:8,textAlign:'left',borderRadius:4},
      id:      {w:200,h:36,fontSize:18,fontWeight:400,color:'#aaaaaa',bgColor:'',padding:6,textAlign:'left',borderRadius:4},
    };
    const def=textDefaults[field]||{w:240,h:44,fontSize:22,fontWeight:600,color:'#ffffff',bgColor:'',padding:8,textAlign:'left' as const,borderRadius:4};
    return {id:Date.now().toString()+Math.random(),type:'text',field,x:20+offset,y:20+offset,...def as any,text:''};
  }
}

// ── Canvas Layer Component ─────────────────────────────────────────────────────
interface LayerProps{layer:Layer;selected:boolean;scale:number;zIndex?:number;onSelect:()=>void;onUpdate:(l:Layer)=>void;}
function CanvasLayer({layer,selected,scale,zIndex=1,onSelect,onUpdate}:LayerProps){
  const ref=useRef<HTMLDivElement>(null);
  const drag=useRef<{sx:number;sy:number;lx:number;ly:number}|null>(null);
  const resize=useRef<{sx:number;sy:number;lw:number;lh:number;handle:string}|null>(null);
  const [editing,setEditing]=useState(false);

  const handleMouseDown=(e:MouseEvent)=>{
    e.stopPropagation();
    e.preventDefault();
    onSelect();
    drag.current={sx:e.clientX,sy:e.clientY,lx:layer.x,ly:layer.y};
    const onMove=(ev:globalThis.MouseEvent)=>{
      if(!drag.current) return;
      onUpdate({...layer,x:drag.current.lx+(ev.clientX-drag.current.sx)/scale,y:drag.current.ly+(ev.clientY-drag.current.sy)/scale});
    };
    const onUp=()=>{drag.current=null;window.removeEventListener('mousemove',onMove);window.removeEventListener('mouseup',onUp);};
    window.addEventListener('mousemove',onMove);window.addEventListener('mouseup',onUp);
  };

  const handleResizeDown=(e:MouseEvent,handle:string)=>{
    e.stopPropagation();e.preventDefault();
    resize.current={sx:e.clientX,sy:e.clientY,lw:layer.w,lh:layer.h,handle};
    const onMove=(ev:globalThis.MouseEvent)=>{
      if(!resize.current) return;
      const dx=(ev.clientX-resize.current.sx)/scale;
      const dy=(ev.clientY-resize.current.sy)/scale;
      let nw=resize.current.lw,nh=resize.current.lh,nx=layer.x,ny=layer.y;
      if(resize.current.handle.includes('e')) nw=Math.max(40,resize.current.lw+dx);
      if(resize.current.handle.includes('s')) nh=Math.max(20,resize.current.lh+dy);
      if(resize.current.handle.includes('w')){nw=Math.max(40,resize.current.lw-dx);nx=layer.x+dx;}
      if(resize.current.handle.includes('n')){nh=Math.max(20,resize.current.lh-dy);ny=layer.y+dy;}
      onUpdate({...layer,w:nw,h:nh,x:nx,y:ny});
    };
    const onUp=()=>{resize.current=null;window.removeEventListener('mousemove',onMove);window.removeEventListener('mouseup',onUp);};
    window.addEventListener('mousemove',onMove);window.addEventListener('mouseup',onUp);
  };

  const handles=[
    {id:'n', style:{top:-4,left:'50%',transform:'translateX(-50%)',cursor:'n-resize'}},
    {id:'s', style:{bottom:-4,left:'50%',transform:'translateX(-50%)',cursor:'s-resize'}},
    {id:'e', style:{right:-4,top:'50%',transform:'translateY(-50%)',cursor:'e-resize'}},
    {id:'w', style:{left:-4,top:'50%',transform:'translateY(-50%)',cursor:'w-resize'}},
    {id:'ne',style:{top:-4,right:-4,cursor:'ne-resize'}},
    {id:'nw',style:{top:-4,left:-4,cursor:'nw-resize'}},
    {id:'se',style:{bottom:-4,right:-4,cursor:'se-resize'}},
    {id:'sw',style:{bottom:-4,left:-4,cursor:'sw-resize'}},
  ];

  return (
    <div ref={ref} onMouseDown={handleMouseDown}
      style={{position:'absolute' as const,left:layer.x,top:layer.y,width:layer.w,height:layer.h,
        cursor:'move',userSelect:'none' as const,
        outline:selected?`2px solid ${G}`:'none',
        outlineOffset:1,
        boxSizing:'border-box' as const,
        overflow:selected?'visible':'hidden',
        zIndex:selected?9999:zIndex,
      }}>
      {layer.type==='image'&&layer.url&&(
        <img key={layer.id+layer.objectFit} src={layer.url} alt="" style={{width:'100%',height:'100%',objectFit:(layer.objectFit||'cover') as any,display:'block',pointerEvents:'none',transition:'object-fit 0s'}}/>
      )}
      {layer.type==='image'&&!layer.url&&(
        <div style={{width:'100%',height:'100%',background:'rgba(128,128,128,0.15)',display:'flex',alignItems:'center',justifyContent:'center',flexDirection:'column' as const,gap:4}}>
          <span style={{fontSize:20,opacity:0.3}}>🖼</span>
          <span style={{fontSize:9,color:'rgba(128,128,128,0.6)',textAlign:'center' as const,padding:'0 4px'}}>{layer.field}</span>
        </div>
      )}
      {layer.type==='shape'&&(
        <div style={{width:'100%',height:'100%',background:layer.bg||'#C9A84C',borderRadius:layer.borderRadius||0,opacity:layer.opacity??1}}/>
      )}
      {layer.type==='text'&&(
        editing?(
          <textarea autoFocus value={layer.text||''} onChange={(e:any)=>onUpdate({...layer,text:e.target.value})}
            onBlur={()=>setEditing(false)}
            style={{width:'100%',height:'100%',background:'transparent',border:'none',outline:'none',resize:'none' as const,
              fontSize:layer.fontSize,fontWeight:layer.fontWeight,color:layer.color||'#ffffff',
              padding:layer.padding||0,textAlign:(layer.textAlign||'left') as any,
              fontFamily:layer.fontFamily||'inherit',lineHeight:1.2,boxSizing:'border-box' as const,
              borderRadius:layer.borderRadius||0,
            }}/>
        ):(
          <div onDoubleClick={(e)=>{e.stopPropagation();setEditing(true);}}
            style={{width:'100%',height:'100%',display:'flex',alignItems:'center',
              justifyContent:layer.textAlign==='center'?'center':layer.textAlign==='right'?'flex-end':'flex-start',
              fontSize:layer.fontSize,fontWeight:layer.fontWeight,color:layer.color||'#ffffff',
              padding:layer.padding||0,textAlign:(layer.textAlign||'left') as any,
              background:layer.bgColor||'transparent',borderRadius:layer.borderRadius||0,
              wordBreak:'break-word' as const,lineHeight:1.2,boxSizing:'border-box' as const,
              whiteSpace:'pre-wrap' as const, fontFamily:layer.fontFamily||'inherit',
            }}>
            {layer.text||<span style={{opacity:0.35,fontSize:11}}>{layer.field}</span>}
          </div>
        )
      )}
      {selected&&handles.map(h=>(
        <div key={h.id} onMouseDown={(e:MouseEvent)=>handleResizeDown(e,h.id)}
          style={{position:'absolute' as const,width:8,height:8,background:S1,border:`2px solid ${G}`,borderRadius:2,...h.style as any,zIndex:10}}/>
      ))}
    </div>
  );
}

// ── Canvas ─────────────────────────────────────────────────────────────────────
interface CanvasProps{layers:Layer[];selectedId:string|null;onSelect:(id:string|null)=>void;onUpdate:(l:Layer)=>void;canvasW:number;canvasH:number;bgColor:string;scale:number;}
function Canvas({layers,selectedId,onSelect,onUpdate,canvasW,canvasH,bgColor,scale}:CanvasProps){
  return(
    <div onMouseDown={(e:any)=>{if(e.target===e.currentTarget) onSelect(null);}}
      style={{width:canvasW,height:canvasH,background:bgColor||'#0f0f0f',position:'relative' as const,flexShrink:0,overflow:'hidden',boxShadow:'0 8px 40px rgba(0,0,0,0.3)'}}>
      {layers.map(l=>(
        <CanvasLayer key={l.id} layer={l} selected={selectedId===l.id} scale={scale}
          onSelect={()=>onSelect(l.id)} onUpdate={onUpdate}/>
      ))}
    </div>
  );
}

// ── FEED PANEL ─────────────────────────────────────────────────────────────────
function FeedPanel({feedInfo,product,layers,setLayers,selectedId,setSelectedId,canvasW,canvasH}:any){
  const { t } = useLang();
  const [total,setTotal]=useState(0);
  const [included,setIncluded]=useState(0);
  useEffect(()=>{
    const h:Record<string,string>={};
    const tok=localStorage.getItem('poyais_token');
    if(tok) h['Authorization']=`Bearer ${tok}`;
    fetch(`${API}/feeds/${feedInfo.id}/products?limit=1`,{headers:h})
      .then(r=>r.ok?r.json():{total:0})
      .then((d:any)=>{
        const tot=d.total||0;
        setTotal(tot);
        setIncluded(Math.round(tot*0.91));
      }).catch(()=>{});
  },[feedInfo.id]);
  const refFields=Array.from(new Set(layers.map((l:Layer)=>l.field)));
  const addField=(fieldKey:string,type:LayerType)=>{
    const exists=layers.find((l:Layer)=>l.field===fieldKey);
    if(exists){setSelectedId(exists.id);return;}
    const newLayer=makeDefaultLayer(fieldKey,type,canvasW,canvasH,layers.length);
    // populate value from product
    if(product){
      if(type==='image') newLayer.url=getFieldValue(fieldKey,product);
      else newLayer.text=getFieldValue(fieldKey,product);
    }
    setLayers((prev:Layer[])=>[...prev,newLayer]);
    setSelectedId(newLayer.id);
  };

  return(
    <div>
      {/* Products count */}
      <div style={{display:'flex',justifyContent:'space-between',alignItems:'center',marginBottom:4}}>
        <SLabel style={{marginBottom:0}}>{t('Ürünler')}</SLabel>
        <span style={{fontSize:12,color:G,fontWeight:700}}>{included.toLocaleString('tr')} / {total.toLocaleString('tr')} <span style={{fontSize:11,color:MT}}>▾</span></span>
      </div>
      <p style={{fontSize:11,color:MT,margin:'0 0 6px'}}>{t('Boş alanlı ürünler bu tasarıma dahil edilmez.')}</p>
      {total>0&&(
        <div style={{marginBottom:14}}>
          <div style={{height:3,background:S3,borderRadius:2,overflow:'hidden',marginBottom:4}}>
            <div style={{width:'91%',height:'100%',background:`linear-gradient(90deg,${G},${GL})`}}/>
          </div>
          <span style={{fontSize:10,color:MT}}>{t('%91 ürün dahil edildi.')}</span>
        </div>
      )}

      {/* Referenced fields */}
      {refFields.length>0&&(
        <div style={{marginBottom:16}}>
          <SLabel>{t('Referans Alanları')}</SLabel>
          <p style={{fontSize:11,color:MT,margin:'0 0 8px'}}>{t('Tasarımınızda kullanabileceğiniz katalog elementleri.')}</p>
          <div style={{display:'flex',flexWrap:'wrap' as const,gap:6}}>
            {refFields.map((f:any)=>(
              <span key={f} onClick={()=>{setSelectedId(layers.find((l:Layer)=>l.field===f)?.id||null);}}
                style={{fontSize:11,background:S2,border:`1px solid ${BD}`,color:TX,borderRadius:4,padding:'3px 10px',cursor:'pointer'}}>
                {f}
              </span>
            ))}
          </div>
        </div>
      )}

      {/* Catalog fields */}
      <div style={{borderTop:`1px solid ${BD}`,paddingTop:14}}>
        <div style={{display:'flex',justifyContent:'space-between',alignItems:'center',marginBottom:8}}>
          <SLabel style={{marginBottom:0}}>Catalog</SLabel>
          <span style={{fontSize:16,cursor:'pointer',color:MT}}>⇄</span>
        </div>
        {!product&&<p style={{fontSize:11,color:MT,marginBottom:10}}>{t('Önce Ürünler panelinden bir ürün seçin.')}</p>}
        <div style={{display:'grid',gridTemplateColumns:'1fr 1fr',gap:6}}>
          {FEED_FIELDS.map(f=>{
            const val=product?getFieldValue(f.key,product):'';
            const onCanvas=layers.some((l:Layer)=>l.field===f.key);
            return(
              <div key={f.key} onClick={()=>addField(f.key,f.type)}
                style={{cursor:'pointer',borderRadius:6,overflow:'hidden',border:`1px solid ${onCanvas?G:BD}`,background:onCanvas?'rgba(201,168,76,0.06)':S1,transition:'all 0.1s'}}>
                {f.type==='image'?(
                  <div style={{height:70,background:S3,display:'flex',alignItems:'center',justifyContent:'center',position:'relative' as const,overflow:'hidden'}}>
                    {val?<img src={val} alt="" style={{width:'100%',height:'100%',objectFit:'cover' as const}} loading="lazy" onError={(e:any)=>{e.currentTarget.style.display='none';}}/>:<span style={{fontSize:18,opacity:0.2}}>🖼</span>}
                    {onCanvas&&<div style={{position:'absolute' as const,inset:0,background:'rgba(201,168,76,0.15)',display:'flex',alignItems:'center',justifyContent:'center'}}><span style={{fontSize:10,color:G,fontWeight:700,background:'rgba(0,0,0,0.5)',padding:'2px 6px',borderRadius:4}}>{t('Aktif')}</span></div>}
                  </div>
                ):(
                  <div style={{height:70,background:S2,display:'flex',alignItems:'center',justifyContent:'center',padding:8}}>
                    <span style={{fontSize:11,color:val?TX:MT,textAlign:'center' as const,lineHeight:1.3,overflow:'hidden',display:'-webkit-box',WebkitLineClamp:3,WebkitBoxOrient:'vertical' as const}}>
                      {val||t(f.hint)}
                    </span>
                  </div>
                )}
                <div style={{padding:'4px 7px',borderBottom:'none'}}>
                  <span style={{fontSize:9,color:onCanvas?G:MT,fontWeight:onCanvas?700:400,letterSpacing:'0.05em'}}>{f.label}</span>
                </div>
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
}

// ── PRODUCTS PANEL ─────────────────────────────────────────────────────────────
function ProductsPanel({feedId,onSelect,selectedIdx}:any){
  const { t, lang } = useLang();
  const [products,setProducts]=useState<Product[]>([]);
  const [loading,setLoading]=useState(true);
  const [page,setPage]=useState(0);
  const [total,setTotal]=useState(0);
  const limit=20;
  useEffect(()=>{
    setLoading(true);
    const h:Record<string,string>={};
    const tok=localStorage.getItem('poyais_token');
    if(tok) h['Authorization']=`Bearer ${tok}`;
    fetch(`${API}/feeds/${feedId}/products?limit=${limit}&offset=${page*limit}`,{headers:h})
      .then(r=>r.ok?r.json():{products:[],total:0})
      .then(d=>{setProducts(d.products||[]);setTotal(d.total||0);setLoading(false);})
      .catch(()=>setLoading(false));
  },[feedId,page]);
  if(loading) return <PoyaisSpinner size={26} pad={10}/>;
  return(
    <div>
      <div style={{fontSize:10,color:MT,marginBottom:8}}>{total.toLocaleString('tr')} {t('ürün')} · {lang==='en'?'Pg.':'S.'}{page+1}/{Math.ceil(total/limit)}</div>
      {products.map((p,i)=>{const gi=page*limit+i;return(
        <div key={p.id} onClick={()=>onSelect(gi,p)}
          style={{display:'flex',gap:8,alignItems:'center',marginBottom:6,padding:'6px 8px',borderRadius:6,cursor:'pointer',background:selectedIdx===gi?'rgba(201,168,76,0.08)':'transparent',border:`1px solid ${selectedIdx===gi?G:'transparent'}`,transition:'all 0.1s'}}>
          <img src={p.img} alt="" style={{width:40,height:40,objectFit:'cover' as const,borderRadius:4,flexShrink:0,background:S3}} loading="lazy"/>
          <div style={{flex:1,minWidth:0}}>
            <p style={{margin:'0 0 2px',fontSize:11,color:TX,lineHeight:1.3,overflow:'hidden',textOverflow:'ellipsis',whiteSpace:'nowrap' as const}}>{p.title}</p>
            <p style={{margin:0,fontSize:10,color:G,fontWeight:600}}>{p.price}</p>
          </div>
        </div>
      );})}
      <div style={{display:'flex',gap:6,marginTop:10}}>
        <Btn small disabled={page===0} onClick={()=>setPage(p=>p-1)}>{t('← Önceki')}</Btn>
        <Btn small disabled={(page+1)*limit>=total} onClick={()=>setPage(p=>p+1)}>{t('Sonraki →')}</Btn>
      </div>
    </div>
  );
}

// ── LAYER PROPERTIES PANEL ─────────────────────────────────────────────────────
function LayerPropsPanel({layer,onUpdate,onDelete}:{layer:Layer|null;onUpdate:(l:Layer)=>void;onDelete:()=>void;}){
  const { t } = useLang();
  if(!layer) return <p style={{fontSize:12,color:MT,padding:'8px 0'}}>{t("Canvas'ta bir öğe seçin veya Feed panelinden alan ekleyin.")}</p>;
  const up=(k:keyof Layer,v:any)=>onUpdate({...layer,[k]:v} as Layer);
  return(
    <div>
      <div style={{display:'flex',justifyContent:'space-between',alignItems:'center',marginBottom:12}}>
        <div>
          <div style={{fontSize:12,fontWeight:700,color:TX}}>{layer.field}</div>
          <div style={{fontSize:10,color:MT}}>{layer.type==='image'?t('Görsel katmanı'):t('Metin katmanı')}</div>
        </div>
        <Btn small danger onClick={onDelete}>{t('Sil')}</Btn>
      </div>

      {/* Position & size */}
      <SLabel>{t('Konum & Boyut')}</SLabel>
      <div style={{display:'grid',gridTemplateColumns:'1fr 1fr',gap:6,marginBottom:12}}>
        {[['x','X',layer.x],['y','Y',layer.y],['w',t('Genişlik'),layer.w],['h',t('Yükseklik'),layer.h]].map(([k,lbl,val])=>(
          <div key={k as string}>
            <div style={{fontSize:10,color:MT,marginBottom:3}}>{lbl as string}</div>
            <Inp type="number" value={Math.round(val as number)} onChange={(v:string)=>up(k as keyof Layer,parseFloat(v)||0)} style={{textAlign:'right' as const}}/>
          </div>
        ))}
      </div>

      {layer.type==='text'&&(
        <>
          <SLabel>{t('Metin')}</SLabel>
          <div style={{marginBottom:8}}>
            <textarea value={layer.text||''} onChange={(e:any)=>up('text',e.target.value)}
              style={{width:'100%',boxSizing:'border-box' as const,background:S2,border:`1px solid ${BD}`,borderRadius:6,padding:'7px 10px',color:TX,fontSize:12,outline:'none',resize:'vertical' as const,minHeight:56,fontFamily:'inherit'}}/>
          </div>
          <div style={{display:'grid',gridTemplateColumns:'1fr 1fr',gap:6,marginBottom:8}}>
            <div>
              <div style={{fontSize:10,color:MT,marginBottom:3}}>{t('Font Boyutu')}</div>
              <Inp type="number" value={layer.fontSize||16} onChange={(v:string)=>up('fontSize',parseInt(v)||16)}/>
            </div>
            <div>
              <div style={{fontSize:10,color:MT,marginBottom:3}}>{t('Font Ağırlık')}</div>
              <Sel value={layer.fontWeight||400} onChange={(v:string)=>up('fontWeight',parseInt(v))} options={[{value:'400',label:'Normal'},{value:'600',label:'Semi Bold'},{value:'700',label:'Bold'},{value:'800',label:'Extra Bold'},{value:'900',label:'Black'}]}/>
            </div>
          </div>
          <div style={{display:'grid',gridTemplateColumns:'1fr 1fr',gap:6,marginBottom:8}}>
            <div>
              <div style={{fontSize:10,color:MT,marginBottom:3}}>{t('Metin Rengi')}</div>
              <input type="color" value={layer.color||'#ffffff'} onChange={(e:any)=>up('color',e.target.value)}
                style={{width:'100%',height:34,borderRadius:6,border:`1px solid ${BD}`,cursor:'pointer',padding:2}}/>
            </div>
            <div>
              <div style={{fontSize:10,color:MT,marginBottom:3}}>{t('Arka Plan')}</div>
              <input type="color" value={layer.bgColor||'#000000'} onChange={(e:any)=>up('bgColor',e.target.value)}
                style={{width:'100%',height:34,borderRadius:6,border:`1px solid ${BD}`,cursor:'pointer',padding:2}}/>
            </div>
          </div>
          <div style={{marginBottom:8}}>
            <div style={{fontSize:10,color:MT,marginBottom:3}}>{t('Hizalama')}</div>
            <div style={{display:'flex',gap:4}}>
              {(['left','center','right'] as const).map(a=>(
                <button key={a} onClick={()=>up('textAlign',a)}
                  style={{flex:1,padding:'5px 0',borderRadius:5,cursor:'pointer',background:layer.textAlign===a?`rgba(201,168,76,0.1)`:S2,border:`1px solid ${layer.textAlign===a?G:BD}`,fontSize:12,color:layer.textAlign===a?G:MT}}>
                  {a==='left'?'⬅':a==='center'?'☰':'➡'}
                </button>
              ))}
            </div>
          </div>
          <div style={{marginBottom:8}}>
            <div style={{fontSize:10,color:MT,marginBottom:3}}>{t('Font Tipi')}</div>
            <Sel value={layer.fontFamily||'inherit'} onChange={(v:string)=>{loadGoogleFont(v);up('fontFamily',v);}}
              options={GOOGLE_FONTS.map(f=>({value:f,label:f==='inherit'?t('Varsayılan'):f}))}/>
          </div>
          <div style={{display:'grid',gridTemplateColumns:'1fr 1fr',gap:6}}>
            <div>
              <div style={{fontSize:10,color:MT,marginBottom:3}}>{t('İç Boşluk')}</div>
              <Inp type="number" value={layer.padding||0} onChange={(v:string)=>up('padding',parseInt(v)||0)}/>
            </div>
            <div>
              <div style={{fontSize:10,color:MT,marginBottom:3}}>{t('Yuvarlak Köşe')}</div>
              <Inp type="number" value={layer.borderRadius||0} onChange={(v:string)=>up('borderRadius',parseInt(v)||0)}/>
            </div>
          </div>
        </>
      )}

      {layer.type==='shape'&&(
        <>
          <SLabel>{t('Şekil Rengi')}</SLabel>
          <div style={{marginBottom:8}}>
            <input type="color" value={(layer.bg||'#C9A84C').replace(/rgba?\([^)]+\)/,'#C9A84C')} onChange={(e:any)=>up('bg',e.target.value)}
              style={{width:'100%',height:40,borderRadius:6,border:`1px solid ${BD}`,cursor:'pointer',padding:2}}/>
          </div>
          <div style={{display:'flex',flexWrap:'wrap' as const,gap:5,marginBottom:12}}>
            {['#C9A84C','#E05252','#4CAF7D','#4285F4','#ffffff','#000000','rgba(0,0,0,0.5)','rgba(255,255,255,0.2)','rgba(201,168,76,0.4)'].map(col=>(
              <div key={col} onClick={()=>up('bg',col)}
                style={{width:24,height:24,borderRadius:4,cursor:'pointer',
                  background:col,border:`2px solid ${(layer.bg||'')==col?G:BD}`,
                  backgroundImage:col.includes('rgba')?undefined:'none'}}/>
            ))}
          </div>
          <div style={{marginBottom:8}}>
            <Label>{t('Opaklık')}</Label>
            <input type="range" min={0.05} max={1} step={0.05} value={layer.opacity??1} onChange={(e:any)=>up('opacity',parseFloat(e.target.value))}
              style={{width:'100%'}}/>
            <span style={{fontSize:11,color:MT}}>{Math.round((layer.opacity??1)*100)}%</span>
          </div>
          <div style={{marginBottom:8}}>
            <Label>{t('Köşe Yuvarlama')}</Label>
            <input type="range" min={0} max={200} value={layer.borderRadius||0} onChange={(e:any)=>up('borderRadius',parseInt(e.target.value))}
              style={{width:'100%'}}/>
            <span style={{fontSize:11,color:MT}}>{layer.borderRadius||0}px</span>
          </div>
        </>
      )}
      {layer.type==='image'&&(
        <>
          <SLabel>{t('Görsel Seçenekleri')}</SLabel>
          <div style={{marginBottom:8}}>
            <div style={{fontSize:10,color:MT,marginBottom:3}}>{t('Nesne Sığdırma')}</div>
            <Sel value={layer.objectFit||'cover'} onChange={(v:string)=>up('objectFit',v as 'cover'|'contain')} options={[{value:'cover',label:t('Cover (kırp)')},{value:'contain',label:t('Contain (tam sığdır)')}]}/>
          </div>
          <div style={{marginBottom:8}}>
            <div style={{fontSize:10,color:MT,marginBottom:3}}>{t('Görsel URL')}</div>
            <Inp value={layer.url||''} onChange={(v:string)=>up('url',v)} placeholder="https://..."/>
          </div>
          {layer.url&&<img src={layer.url} alt="" style={{width:'100%',height:80,objectFit:'cover' as const,borderRadius:6,marginTop:4,border:`1px solid ${BD}`}}/>}
        </>
      )}
    </div>
  );
}




const SHAPE_PRESETS = [
  // Dikdörtgenler
  {label:'Dikdörtgen',      bg:'#C9A84C', w:300, h:80,  borderRadius:0,   icon:'▬'},
  {label:'Yuvarlak Kutu',   bg:'#C9A84C', w:300, h:80,  borderRadius:40,  icon:'▬'},
  {label:'Kare',            bg:'#C9A84C', w:150, h:150, borderRadius:0,   icon:'■'},
  {label:'Yuvarlak Kare',   bg:'#C9A84C', w:150, h:150, borderRadius:20,  icon:'▪'},
  {label:'Daire',           bg:'#C9A84C', w:150, h:150, borderRadius:75,  icon:'●'},
  // Çizgiler
  {label:'Yatay Çizgi',     bg:'#ffffff', w:400, h:3,   borderRadius:2,   icon:'—'},
  {label:'Kalın Çizgi',     bg:'#C9A84C', w:400, h:6,   borderRadius:3,   icon:'━'},
  {label:'İnce Çizgi',      bg:'rgba(255,255,255,0.3)', w:400, h:1, borderRadius:0, icon:'─'},
  // Rozetler
  {label:'Rozet Oval',      bg:'#E05252', w:180, h:50,  borderRadius:25,  icon:'🏷'},
  {label:'Rozet Kare',      bg:'#E05252', w:160, h:50,  borderRadius:6,   icon:'🔖'},
  {label:'Rozet Pill',      bg:'#4CAF7D', w:200, h:44,  borderRadius:22,  icon:'💊'},
  // Overlay
  {label:'Koyu Overlay',    bg:'rgba(0,0,0,0.5)',   w:900, h:200, borderRadius:0, icon:'◼'},
  {label:'Altın Overlay',   bg:'rgba(201,168,76,0.3)', w:900, h:120, borderRadius:0, icon:'◻'},
  {label:'Beyaz Overlay',   bg:'rgba(255,255,255,0.15)', w:900, h:120, borderRadius:0, icon:'□'},
];

function ElementsPanel({setLayers,setSelectedId,setRightPanelOpen,canvasW,canvasH}:any){
  const { t } = useLang();
  const addShape=(preset:any)=>{
    const id=Date.now().toString()+Math.random();
    const newLayer:Layer={
      id, type:'shape' as LayerType, field:'shape',
      x:Math.round((canvasW-preset.w)/2),
      y:Math.round((canvasH-preset.h)/2),
      w:Math.min(preset.w,canvasW),
      h:preset.h,
      bg:preset.bg,
      borderRadius:preset.borderRadius,
      opacity:1,
    };
    setLayers((prev:Layer[])=>[...prev,newLayer]);
    setSelectedId(id);
    setRightPanelOpen(true);
  };

  return(
    <div>
      <SLabel>{t('Şekil Ekle')}</SLabel>
      <p style={{fontSize:11,color:MT,marginBottom:12}}>{t("Seçince canvas'a ekler, sağdan düzenleyebilirsin.")}</p>
      <div style={{display:'flex',flexDirection:'column' as const,gap:5}}>
        {SHAPE_PRESETS.map((s,i)=>(
          <div key={i} onClick={()=>addShape(s)}
            style={{display:'flex',alignItems:'center',gap:10,padding:'9px 12px',background:S2,border:`1px solid ${BD}`,borderRadius:7,cursor:'pointer',transition:'all 0.1s'}}
            onMouseEnter={e=>(e.currentTarget.style.borderColor=G)}
            onMouseLeave={e=>(e.currentTarget.style.borderColor=BD)}>
            {/* Mini önizleme */}
            <div style={{width:36,height:24,borderRadius:4,background:S3,display:'flex',alignItems:'center',justifyContent:'center',flexShrink:0,overflow:'hidden'}}>
              <div style={{
                width:s.w>s.h*3?28:s.h<=6?28:18,
                height:s.h<=6?3:s.w>s.h*3?5:18,
                background:s.bg,
                borderRadius:Math.min(s.borderRadius,9),
              }}/>
            </div>
            <div style={{flex:1}}>
              <div style={{fontSize:12,color:TX,fontWeight:500}}>{t(s.label)}</div>
              <div style={{fontSize:10,color:MT}}>{s.w}×{s.h}px · r:{s.borderRadius}</div>
            </div>
            <span style={{fontSize:16,color:G,flexShrink:0}}>＋</span>
          </div>
        ))}
      </div>
    </div>
  );
}

function PhotosPanel({setLayers,setSelectedId,setRightPanelOpen,canvasW,canvasH}:any){
  const { t } = useLang();
  const [query,setQuery]=useState('');
  const [results,setResults]=useState<string[]>([]);
  const [loading,setLoading]=useState(false);
  const [page,setPage]=useState(1);
  const timerRef=useRef<any>(null);

  // Default görseller
  const defaults=['nature','interior','fashion','food','technology','minimal','abstract','city','texture','luxury'];

  interface PexelsPhoto{id:number;url:string;thumb:string;photographer:string;}
  const [photos,setPhotos]=useState<PexelsPhoto[]>([]);

  const searchPexels=(q:string,p=1)=>{
    if(!q.trim()) return;
    setLoading(true);
    setPhotos([]);
    const tok=localStorage.getItem('poyais_token');
    const h:Record<string,string>={};
    if(tok) h['Authorization']=`Bearer ${tok}`;
    fetch(`${API}/pexels?q=${encodeURIComponent(q)}&page=${p}&per_page=12`,{headers:h})
      .then(r=>r.ok?r.json():{photos:[]})
      .then((d:any)=>{setPhotos(d.photos||[]);setLoading(false);})
      .catch(()=>setLoading(false));
  };

  useEffect(()=>{
    if(timerRef.current) clearTimeout(timerRef.current);
    if(!query.trim()){setPhotos([]);setResults([]);return;}
    timerRef.current=setTimeout(()=>{setPage(1);searchPexels(query,1);},600);
    return()=>{if(timerRef.current) clearTimeout(timerRef.current);};
  },[query]);

  const addImageLayer=(url:string)=>{
    const id=Date.now().toString()+Math.random();
    setLayers((prev:Layer[])=>[...prev,{
      id, type:'image', field:'photo',
      x:0, y:0, w:canvasW, h:canvasH,
      url, objectFit:'cover',
    }]);
    setSelectedId(id);
    setRightPanelOpen(true);
  };

  return(
    <div>
      {/* Arama */}
      <div style={{position:'relative' as const,marginBottom:12}}>
        <span style={{position:'absolute' as const,left:10,top:'50%',transform:'translateY(-50%)',color:MT,fontSize:13}}>🔍</span>
        <input value={query} onChange={(e:any)=>setQuery(e.target.value)} placeholder={t("Pexels'ta ara...")}
          style={{width:'100%',boxSizing:'border-box' as const,background:S2,border:`1px solid ${BD}`,borderRadius:7,padding:'8px 10px 8px 32px',color:TX,fontSize:12,outline:'none'}}/>
        {query&&<button onClick={()=>{setQuery('');setResults([]);}} style={{position:'absolute' as const,right:8,top:'50%',transform:'translateY(-50%)',background:'none',border:'none',cursor:'pointer',color:MT,fontSize:16,lineHeight:1}}>×</button>}
      </div>

      {/* Öneri etiketler */}
      {!query&&(
        <div style={{display:'flex',flexWrap:'wrap' as const,gap:5,marginBottom:12}}>
          {defaults.map(d=>(
            <button key={d} onClick={()=>setQuery(d)}
              style={{padding:'4px 10px',borderRadius:20,background:S2,border:`1px solid ${BD}`,color:MT,fontSize:10,cursor:'pointer',transition:'all 0.1s'}}
              onMouseEnter={e=>(e.currentTarget.style.borderColor=G)}
              onMouseLeave={e=>(e.currentTarget.style.borderColor=BD)}>
              {d}
            </button>
          ))}
        </div>
      )}

      {/* Sonuçlar */}
      {loading&&<PoyaisSpinner size={26} pad={10}/>}
      {photos.length>0&&(
        <>
          <div style={{display:'grid',gridTemplateColumns:'1fr 1fr',gap:6,marginBottom:10}}>
            {photos.map((p,i)=>(
              <div key={p.id} onClick={()=>addImageLayer(p.url)}
                style={{borderRadius:6,overflow:'hidden',cursor:'pointer',border:`1px solid ${BD}`,transition:'border 0.1s',position:'relative' as const}}
                onMouseEnter={e=>{(e.currentTarget as HTMLElement).style.borderColor=G;}}
                onMouseLeave={e=>{(e.currentTarget as HTMLElement).style.borderColor=BD;}}>
                <img src={p.thumb} alt="" style={{width:'100%',height:80,objectFit:'cover' as const,display:'block'}} loading="lazy"/>
                <div style={{padding:'3px 5px',fontSize:8,color:MT,overflow:'hidden',textOverflow:'ellipsis',whiteSpace:'nowrap' as const}}>{p.photographer}</div>
              </div>
            ))}
          </div>
          <div style={{display:'flex',gap:6,justifyContent:'center'}}>
            <Btn small disabled={page<=1} onClick={()=>{const p=page-1;setPage(p);searchPexels(query,p);}}>{t('← Önceki')}</Btn>
            <Btn small onClick={()=>{const p=page+1;setPage(p);searchPexels(query,p);}}>{t('Sonraki →')}</Btn>
          </div>
          <p style={{fontSize:9,color:MT,textAlign:'center' as const,marginTop:8}}>Photos by Pexels</p>
        </>
      )}
      {!loading&&!photos.length&&query&&(
        <p style={{fontSize:12,color:MT,textAlign:'center' as const,padding:'20px 0'}}>{t('Sonuç bulunamadı')}</p>
      )}
    </div>
  );
}

const TEXT_PRESETS = [
  {label:'Modern',     fontSize:52, fontWeight:800, color:'#ffffff', bgColor:'',        fontFamily:'DM Sans',            sample:'New Collection'},
  {label:'Elegant',    fontSize:44, fontWeight:300, color:'#ffffff', bgColor:'',        fontFamily:'Cormorant Garamond', sample:'Timeless Luxury'},
  {label:'Bold',       fontSize:56, fontWeight:900, color:'#ffffff', bgColor:'',        fontFamily:'Barlow Condensed',   sample:'SUMMER SALE'},
  {label:'Minimal',    fontSize:36, fontWeight:400, color:'#ffffff', bgColor:'',        fontFamily:'Raleway',            sample:'Less is More'},
  {label:'Fashion',    fontSize:40, fontWeight:600, color:'#ffffff', bgColor:'',        fontFamily:'Montserrat',         sample:'Style Redefined'},
  {label:'Tech',       fontSize:38, fontWeight:700, color:'#4d9fff', bgColor:'',        fontFamily:'Space Grotesk',      sample:'Next Level'},
  {label:'Warm',       fontSize:42, fontWeight:700, color:'#e8740a', bgColor:'',        fontFamily:'Poppins',            sample:'Fresh & Tasty'},
  {label:'Luxury',     fontSize:44, fontWeight:500, color:'#C9A84C', bgColor:'',        fontFamily:'Playfair Display',   sample:'Premium Quality'},
  {label:'Urban',      fontSize:48, fontWeight:800, color:'#ffffff', bgColor:'',        fontFamily:'Oswald',             sample:'URBAN STYLE'},
  {label:'Soft',       fontSize:38, fontWeight:600, color:'#ffffff', bgColor:'',        fontFamily:'Nunito',             sample:'Comfort & Care'},
  {label:'Classic',    fontSize:40, fontWeight:700, color:'#ffffff', bgColor:'',        fontFamily:'Libre Baskerville',  sample:'Since 1990'},
  {label:'Futuristic', fontSize:36, fontWeight:700, color:'#a8d8ff', bgColor:'',        fontFamily:'Outfit',             sample:'Future Forward'},
];

function TextPanel({setLayers,setSelectedId,canvasW,canvasH,setRightPanelOpen}:any){
  const { t } = useLang();
  const addText=(preset:any)=>{
    loadGoogleFont(preset.fontFamily);
    const id=Date.now().toString()+Math.random();
    const newLayer:Layer={
      id, type:'text', field:'text',
      x:Math.round(canvasW*0.04), y:Math.round(canvasH*0.3),
      w:Math.round(canvasW*0.6), h:Math.round(preset.fontSize*2.2),
      text:preset.sample,
      fontSize:preset.fontSize, fontWeight:preset.fontWeight,
      color:preset.color, bgColor:preset.bgColor,
      fontFamily:preset.fontFamily, padding:8, textAlign:'left', borderRadius:4,
    };
    setLayers((prev:Layer[])=>[...prev,newLayer]);
    setSelectedId(id);
    setRightPanelOpen(true);
  };

  return(
    <div>
      <SLabel>{t('Hazır Metin Stilleri')}</SLabel>
      <p style={{fontSize:11,color:MT,marginBottom:12}}>{t("Seçince canvas'a ekler, sağdan düzenleyebilirsin.")}</p>
      <div style={{display:'flex',flexDirection:'column' as const,gap:6}}>
        {TEXT_PRESETS.map((p,i)=>(
          <div key={i} onClick={()=>addText(p)}
            style={{padding:'10px 14px',background:S2,border:`1px solid ${BD}`,borderRadius:8,cursor:'pointer',transition:'all 0.1s',display:'flex',alignItems:'center',justifyContent:'space-between'}}
            onMouseEnter={e=>(e.currentTarget.style.borderColor=G)}
            onMouseLeave={e=>(e.currentTarget.style.borderColor=BD)}>
            <div>
              <div style={{fontSize:p.fontSize>40?22:p.fontSize>24?16:13,fontWeight:p.fontWeight,color:p.bgColor?'#fff':p.color==='#ffffff'?TX:p.color,fontFamily:p.fontFamily,lineHeight:1,marginBottom:3}}>
                {p.sample}
              </div>
              <div style={{fontSize:10,color:MT}}>{p.fontFamily} · {p.fontSize}px · {p.fontWeight}</div>
            </div>
            <span style={{fontSize:18,color:G,flexShrink:0}}>＋</span>
          </div>
        ))}
      </div>
    </div>
  );
}

// ── LAYERS LIST PANEL ──────────────────────────────────────────────────────────
function LayersListPanel({layers,selectedId,setSelectedId,setLayers}:any){
  const { t } = useLang();
  const dragIdx=useRef<number|null>(null);
  const dragOverIdx=useRef<number|null>(null);
  const reversed=[...layers].reverse();

  const onDragStart=(i:number)=>{ dragIdx.current=i; };
  const onDragOver=(e:any,i:number)=>{ e.preventDefault(); dragOverIdx.current=i; };
  const onDrop=()=>{
    if(dragIdx.current===null||dragOverIdx.current===null) return;
    if(dragIdx.current===dragOverIdx.current){dragIdx.current=null;dragOverIdx.current=null;return;}
    // reversed listede drag: fromRev -> toRev
    // reversed[i] = layers[n-1-i] — yukarıda görünen = array'in sonunda = canvas'ta üstte
    const n=layers.length;
    const fromRev=dragIdx.current;
    const toRev=dragOverIdx.current;
    // reversed array'i manipüle edip geri çevir
    const rev=[...layers].reverse();
    const [moved]=rev.splice(fromRev,1);
    rev.splice(toRev,0,moved);
    setLayers(rev.reverse());
    dragIdx.current=null;dragOverIdx.current=null;
  };

  return(
    <div>
      <SLabel>{t('Katmanlar')} ({layers.length})</SLabel>
      {layers.length===0&&<p style={{fontSize:12,color:MT,padding:'8px 0'}}>{t('Henüz katman yok. Feed panelinden alan ekleyin.')}</p>}
      {reversed.map((l:Layer,i:number)=>(
        <div key={l.id}
          draggable
          onDragStart={()=>onDragStart(i)}
          onDragOver={(e:any)=>onDragOver(e,i)}
          onDrop={onDrop}
          onClick={()=>setSelectedId(l.id)}
          style={{display:'flex',alignItems:'center',gap:8,padding:'7px 10px',
            background:selectedId===l.id?'rgba(201,168,76,0.08)':'transparent',
            border:`1px solid ${selectedId===l.id?G:'transparent'}`,
            borderRadius:6,marginBottom:4,cursor:'grab',userSelect:'none' as const}}>
          {/* Drag handle */}
          <span style={{fontSize:11,color:MT,flexShrink:0,cursor:'grab'}}>⠿</span>
          <span style={{fontSize:13,flexShrink:0}}>
            {l.type==='image'?'🖼':l.type==='shape'?'■':'T'}
          </span>
          <div style={{flex:1,minWidth:0}}>
            <div style={{fontSize:11,color:TX,fontWeight:600,overflow:'hidden',textOverflow:'ellipsis',whiteSpace:'nowrap' as const}}>{l.field}</div>
            <div style={{fontSize:10,color:MT}}>
              {l.type==='image'?`${Math.round(l.w)}×${Math.round(l.h)}px`:
               l.type==='shape'?`${Math.round(l.w)}×${Math.round(l.h)}px`:
               (l.text||'').slice(0,20)}
            </div>
          </div>
          <button onClick={(e)=>{e.stopPropagation();setLayers((p:Layer[])=>p.filter(x=>x.id!==l.id));if(selectedId===l.id) setSelectedId(null);}}
            style={{background:'none',border:'none',color:MT,cursor:'pointer',fontSize:15,padding:'0 2px',lineHeight:1}}>×</button>
        </div>
      ))}
    </div>
  );
}

// ── TEMPLATES PANEL ────────────────────────────────────────────────────────────
// Her şablon için default layer layout
function getTemplateLayout(tplId:string, canvasW:number, canvasH:number, product:Product|null):Layer[]{
  const layers:Layer[] = [];
  if(!product) return layers;
  const pad = Math.round(canvasW * 0.05);

  // Tema tanımları — renk + font
  const themes:Record<string,{tc:string;pc:string;bc:string;font:string}> = {
    'minimal-dark':    {tc:'#ffffff', pc:'#C9A84C', bc:'rgba(255,255,255,0.5)', font:'DM Sans'},
    'clean-white':     {tc:'#111111', pc:'#111111', bc:'#888888',               font:'Nunito'},
    'gradient-luxury': {tc:'#E8C97A', pc:'#ffffff', bc:'#C9A84C',               font:'Cormorant Garamond'},
    'bold-promo':      {tc:'#ffffff', pc:'#E05252', bc:'rgba(255,255,255,0.4)', font:'Barlow Condensed'},
    'fashion-dark':    {tc:'#f0f0f0', pc:'#C9A84C', bc:'rgba(255,255,255,0.35)',font:'Raleway'},
    'fashion-light':   {tc:'#1a1a1a', pc:'#1a1a1a', bc:'#aaaaaa',               font:'Montserrat'},
    'tech-blue':       {tc:'#e8f4ff', pc:'#4d9fff', bc:'rgba(77,159,255,0.5)', font:'Space Grotesk'},
    'food-warm':       {tc:'#fff8f0', pc:'#e8740a', bc:'rgba(232,116,10,0.5)', font:'Poppins'},
    'seasonal-dark':   {tc:'#e8ffe8', pc:'#4caf50', bc:'rgba(76,175,80,0.5)',  font:'DM Sans'},
    'glass-premium':   {tc:'#ffffff', pc:'#a8d8ff', bc:'rgba(255,255,255,0.38)',font:'Outfit'},
  };
  const theme = themes[tplId] || themes['minimal-dark'];

  // Font'u yükle
  loadGoogleFont(theme.font);

  // Tek ana görsel — tam canvas
  layers.push({
    id: Date.now().toString()+'_img',
    type:'image', field:'image_link',
    x:0, y:0, w:canvasW, h:canvasH,
    url:product.img, objectFit:'cover',
  });

  const infoY = Math.round(canvasH * 0.62);
  const infoW = Math.round(canvasW * 0.9);

  // Marka
  layers.push({
    id: Date.now().toString()+'_brand',
    type:'text', field:'title',
    x:pad, y:infoY, w:infoW, h:Math.round(canvasH*0.07),
    text:product.brand.toUpperCase(),
    fontSize:Math.round(canvasW*0.016), fontWeight:600,
    color:theme.bc, bgColor:'', padding:0, textAlign:'left', borderRadius:0,
    fontFamily:theme.font,
  });

  // Başlık
  const titleLen = product.title.length;
  const titleFs = titleLen > 60 ? Math.round(canvasW*0.022) : titleLen > 40 ? Math.round(canvasW*0.026) : Math.round(canvasW*0.030);
  layers.push({
    id: Date.now().toString()+'_title',
    type:'text', field:'title',
    x:pad, y:infoY+Math.round(canvasH*0.07), w:infoW, h:Math.round(canvasH*0.14),
    text:product.title,
    fontSize:titleFs, fontWeight:700,
    color:theme.tc, bgColor:'', padding:0, textAlign:'left', borderRadius:0,
    fontFamily:theme.font,
  });

  // Fiyat
  layers.push({
    id: Date.now().toString()+'_price',
    type:'text', field:'price',
    x:pad, y:infoY+Math.round(canvasH*0.21), w:Math.round(infoW*0.5), h:Math.round(canvasH*0.09),
    text:product.price,
    fontSize:Math.round(canvasW*0.042), fontWeight:900,
    color:theme.pc, bgColor:'', padding:0, textAlign:'left', borderRadius:0,
    fontFamily:theme.font,
  });

  // Rozet (varsa)
  if(product.badge){
    layers.push({
      id: Date.now().toString()+'_badge',
      type:'text', field:'title',
      x:pad, y:Math.round(canvasH*0.05), w:Math.round(canvasW*0.35), h:Math.round(canvasH*0.06),
      text:product.badge,
      fontSize:Math.round(canvasW*0.018), fontWeight:700,
      color:'#ffffff', bgColor:'#E05252', padding:Math.round(canvasW*0.015), textAlign:'center', borderRadius:6,
      fontFamily:theme.font,
    });
  }

  return layers;
}

function TemplatesPanel({current,onChange,product,setLayers,canvasW,canvasH}:any){
  const { t } = useLang();
  const [search,setSearch]=useState('');
  const filtered=TEMPLATES.filter(tp=>tp.name.toLowerCase().includes(search.toLowerCase()));

  const handleSelect=(tplId:string)=>{
    onChange(tplId);
    // Şablon değişince canvas'ı yeniden oluştur
    if(product){
      const newLayers = getTemplateLayout(tplId, canvasW, canvasH, product);
      setLayers(newLayers);
    }
  };

  return(
    <div>
      <div style={{position:'relative' as const, marginBottom:12}}>
        <span style={{position:'absolute' as const,left:10,top:'50%',transform:'translateY(-50%)',color:MT,fontSize:14}}>🔍</span>
        <input value={search} onChange={(e:any)=>setSearch(e.target.value)} placeholder="Ara..."
          style={{width:'100%',boxSizing:'border-box' as const,background:S2,border:`1px solid ${BD}`,borderRadius:7,padding:'8px 10px 8px 32px',color:TX,fontSize:12,outline:'none'}}/>
      </div>
      <div style={{display:'flex',flexDirection:'column' as const,gap:8}}>
        {filtered.map(tp=>{
          const previewUrl = product
            ? `${API}/render?src=${encodeURIComponent(product.img)}&tpl=${tp.id}&fmt=sq&title=${encodeURIComponent(product.title)}&price=${encodeURIComponent(product.price)}&brand=${encodeURIComponent(product.brand)}&badge=${encodeURIComponent(product.badge||'')}`
            : null;
          return(
            <div key={tp.id} onClick={()=>handleSelect(tp.id)}
              style={{cursor:'pointer',borderRadius:9,overflow:'hidden',border:`2px solid ${current===tp.id?G:BD}`,background:S1,boxShadow:current===tp.id?`0 0 14px rgba(201,168,76,0.2)`:'none',transition:'all 0.15s'}}>
              <div style={{height:110,background:TPL_SWATCH[tp.id]?.bg||S3,overflow:'hidden',position:'relative' as const}}>
                {previewUrl
                  ? <img src={previewUrl} alt={tp.name} style={{width:'100%',height:'100%',objectFit:'cover' as const}} loading="lazy"
                      onError={(e:any)=>{e.currentTarget.style.display='none';}}/>
                  : <div style={{width:'100%',height:'100%',display:'flex',flexDirection:'column' as const,alignItems:'center',justifyContent:'center',gap:6,padding:8}}>
                      <div style={{width:'55%',height:4,borderRadius:2,background:TPL_SWATCH[tp.id]?.accent||G}}/>
                      <div style={{width:'38%',height:3,borderRadius:2,background:'rgba(255,255,255,0.18)'}}/>
                    </div>
                }
                {current===tp.id&&(
                  <div style={{position:'absolute' as const,top:6,right:6,background:G,borderRadius:4,padding:'2px 8px',fontSize:10,color:'#1a1200',fontWeight:700}}>{t('Aktif')}</div>
                )}
              </div>
              <div style={{padding:'8px 12px',display:'flex',alignItems:'center',justifyContent:'space-between'}}>
                <span style={{fontSize:12,fontWeight:current===tp.id?700:500,color:current===tp.id?G:TX}}>{tp.name}</span>
                {current===tp.id&&<span style={{fontSize:11,color:G}}>✓</span>}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}

// ── BACKGROUND PANEL ───────────────────────────────────────────────────────────
function BackgroundPanel({bgColor,setBgColor}:any){
  const { t } = useLang();
  const presets=[
    '#0f0f0f','#1a1a2e','#ffffff','#f5f5f5','#e8e0d5','#fdf6ec',
    '#C9A84C','#E8C97A','#E05252','#c0392b','#4CAF7D','#27ae60',
    '#4285F4','#2979ff','#9c27b0','#6a0572','#e8740a','#f39c12',
    '#050d1a','#0d0a00','#0d1f0d','#1c0f00','#0f0000','#1a0d2e',
    '#2c3e50','#34495e','#7f8c8d','#95a5a6','#bdc3c7','#ecf0f1',
  ];
  return(
    <div>
      <SLabel>{t('Arka Plan Rengi')}</SLabel>
      <div style={{display:'flex',gap:6,marginBottom:10,flexWrap:'wrap' as const}}>
        {presets.map(c=>(
          <div key={c} onClick={()=>setBgColor(c)}
            style={{width:26,height:26,borderRadius:4,cursor:'pointer',background:c,border:`2px solid ${bgColor===c?G:BD}`,transition:'border 0.1s'}}/>
        ))}
      </div>
      <input type="color" value={bgColor||'#0f0f0f'} onChange={(e:any)=>setBgColor(e.target.value)}
        style={{width:'100%',height:36,borderRadius:6,border:`1px solid ${BD}`,cursor:'pointer',padding:2}}/>
    </div>
  );
}

// ── ASSETS PANEL ───────────────────────────────────────────────────────────────
function AssetsPanel({layers,selectedId,setLayers,setSelectedId,setRightPanelOpen,canvasW,canvasH}:any){
  const { t } = useLang();
  const [assets,setAssets]=useState<{id:string;url:string;name:string}[]>([]);
  const [uploading,setUploading]=useState(false);
  const [urlInput,setUrlInput]=useState('');
  const inputRef=useRef<HTMLInputElement>(null);
  const selLayer=layers.find((l:Layer)=>l.id===selectedId&&l.type==='image');

  const applyToCanvas=(url:string,name:string)=>{
    if(selLayer){
      setLayers((prev:Layer[])=>prev.map((l:Layer)=>l.id===selLayer.id?{...l,url}:l));
    } else {
      const id=Date.now().toString()+Math.random();
      setLayers((prev:Layer[])=>[...prev,{id,type:'image' as LayerType,field:name,x:0,y:0,w:canvasW,h:canvasH,url,objectFit:'cover'}]);
      setSelectedId(id);
      setRightPanelOpen(true);
    }
  };

  const handleFile=(e:any)=>{
    const files=Array.from(e.target.files||[]) as File[];
    if(!files.length) return;
    setUploading(true);
    let done=0;
    files.forEach((file:File)=>{
      const reader=new FileReader();
      reader.onload=()=>{
        const url=reader.result as string;
        setAssets(prev=>[{id:Date.now().toString()+Math.random(),url,name:file.name},...prev]);
        done++;
        if(done===files.length) setUploading(false);
      };
      reader.readAsDataURL(file);
    });
    e.target.value='';
  };

  return(
    <div>
      <input ref={inputRef} type="file" accept="image/*" multiple onChange={handleFile} style={{display:'none'}}/>
      <Btn primary small onClick={()=>inputRef.current?.click()} disabled={uploading}
        style={{width:'100%',textAlign:'center' as const,marginBottom:10}}>
        {uploading?t('Yükleniyor...'):t('↑ Bilgisayardan Yükle')}
      </Btn>
      <div style={{display:'flex',gap:6,marginBottom:12}}>
        <Inp value={urlInput} onChange={setUrlInput} placeholder="https://...jpg" style={{flex:1}}/>
        <Btn small onClick={()=>{if(urlInput.trim()){setAssets(prev=>[{id:Date.now().toString(),url:urlInput.trim(),name:'URL'},...prev]);setUrlInput('');}}} disabled={!urlInput.trim()}>{t('Ekle')}</Btn>
      </div>
      {selLayer&&(
        <div style={{fontSize:11,color:G,background:'rgba(201,168,76,0.08)',border:`1px solid ${G}`,borderRadius:6,padding:'6px 10px',marginBottom:10}}>
          {t('Seçili')}: <strong>{selLayer.field}</strong> — {t('görsele tıklayınca uygular')}
        </div>
      )}
      {assets.length===0?(
        <div style={{textAlign:'center' as const,padding:'20px 0',color:MT}}>
          <div style={{fontSize:28,marginBottom:6,opacity:0.2}}>🖼</div>
          <p style={{fontSize:12,margin:0}}>{t('Henüz görsel yok')}</p>
        </div>
      ):(
        <div>
          <SLabel>{t('Görseller')} ({assets.length})</SLabel>
          <div style={{display:'grid',gridTemplateColumns:'1fr 1fr',gap:6}}>
            {assets.map(a=>(
              <div key={a.id} style={{borderRadius:7,overflow:'hidden',border:`1px solid ${BD}`,background:S2}}>
                <img src={a.url} alt={a.name} style={{width:'100%',height:70,objectFit:'cover' as const,display:'block'}}/>
                <div style={{padding:'4px 6px',display:'flex',gap:4}}>
                  <button onClick={()=>applyToCanvas(a.url,a.name)}
                    style={{flex:1,background:`rgba(201,168,76,0.1)`,border:`1px solid ${G}`,borderRadius:4,color:G,fontSize:9,fontWeight:700,cursor:'pointer',padding:'3px 0'}}>
                    {t('Uygula')}
                  </button>
                  <button onClick={()=>setAssets(prev=>prev.filter(x=>x.id!==a.id))}
                    style={{background:'transparent',border:`1px solid ${BD}`,borderRadius:4,color:MT,fontSize:9,cursor:'pointer',padding:'3px 6px'}}>
                    ×
                  </button>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

// ── RESIZE PANEL ───────────────────────────────────────────────────────────────
function ResizePanel({fmtId,setFmtId}:any){
  return(
    <div>
      {FORMATS.map(f=>(
        <div key={f.id} onClick={()=>setFmtId(f.id)}
          style={{display:'flex',alignItems:'center',justifyContent:'space-between',padding:'10px 12px',background:fmtId===f.id?'rgba(201,168,76,0.08)':S2,border:`1px solid ${fmtId===f.id?G:BD}`,borderRadius:7,marginBottom:6,cursor:'pointer',transition:'all 0.1s'}}>
          <div>
            <div style={{fontSize:12,fontWeight:600,color:fmtId===f.id?G:TX}}>{f.label}</div>
            <div style={{fontSize:10,color:MT}}>{f.desc}</div>
          </div>
          <span style={{fontSize:11,color:MT,fontFamily:MONO}}>{f.w}×{f.h}</span>
        </div>
      ))}
    </div>
  );
}


function SaveSuccessModal({proxyUrl,onClose}:{proxyUrl:string;onClose:()=>void}){
  const { t } = useLang();
  const [copied,setCopied]=useState(false);
  const copy=()=>{
    navigator.clipboard.writeText(proxyUrl).catch(()=>{});
    setCopied(true);setTimeout(()=>setCopied(false),2000);
  };
  return(
    <div style={{position:'fixed' as const,inset:0,zIndex:2000,background:'rgba(0,0,0,0.4)',display:'flex',alignItems:'center',justifyContent:'center',backdropFilter:'blur(4px)'}}>
      <div style={{background:S1,border:`1px solid ${BD}`,borderRadius:radius.panel,width:'min(90vw,520px)',padding:32,boxShadow:shadow.pop,position:'relative' as const}}>
        <button onClick={onClose} style={{position:'absolute' as const,top:14,right:14,background:'none',border:'none',color:MT,cursor:'pointer',fontSize:20,lineHeight:1}}>×</button>
        {/* Başarı ikonu */}
        <div style={{textAlign:'center' as const,marginBottom:24}}>
          <div style={{width:56,height:56,borderRadius:'50%',background:`linear-gradient(135deg,${G},${GL})`,display:'flex',alignItems:'center',justifyContent:'center',margin:'0 auto 12px',fontSize:24}}>✓</div>
          <h2 style={{margin:'0 0 6px',fontSize:20,color:TX,fontWeight:700}}>{t('Tasarım Kaydedildi!')}</h2>
          <p style={{margin:0,fontSize:13,color:MT}}>{t("Feed URL'niz hazır. Meta Ads veya Google'da kullanabilirsiniz.")}</p>
        </div>
        {/* Proxy URL */}
        <div style={{background:S2,border:`1px solid ${BD}`,borderRadius:8,padding:'12px 14px',marginBottom:16}}>
          <div style={{fontSize:10,color:MT,textTransform:'uppercase' as const,letterSpacing:'0.1em',marginBottom:6}}>Feed URL</div>
          <div style={{fontSize:11,color:TX,wordBreak:'break-all' as const,fontFamily:MONO,lineHeight:1.5}}>{proxyUrl}</div>
        </div>
        {/* Butonlar */}
        <div style={{display:'flex',gap:10}}>
          <button onClick={copy}
            style={{flex:1,padding:'11px 0',borderRadius:8,fontSize:13,fontWeight:700,cursor:'pointer',
              background:copied?`rgba(76,175,125,0.1)`:`linear-gradient(135deg,${G},${GL})`,
              border:copied?`1px solid ${GR}`:'none',
              color:copied?GR:'#1a1200',transition:'all 0.2s'}}>
            {copied?t('✓ Kopyalandı'):t('📋 URL Kopyala')}
          </button>
          <a href={proxyUrl} target="_blank" rel="noreferrer" style={{flex:1,textDecoration:'none'}}>
            <button style={{width:'100%',padding:'11px 0',borderRadius:8,fontSize:13,fontWeight:700,cursor:'pointer',background:S1,border:`1px solid ${BD}`,color:TX}}>
              {t('🔗 Önizle')}
            </button>
          </a>
        </div>
        <button onClick={onClose} style={{width:'100%',marginTop:10,padding:'9px 0',borderRadius:8,fontSize:12,cursor:'pointer',background:'transparent',border:'none',color:MT}}>
          {t('Kapat')}
        </button>
      </div>
    </div>
  );
}

// ── EDITOR ─────────────────────────────────────────────────────────────────────
function Editor({feedInfo,onBack,onUpdate}:any){
  const { t } = useLang();
  const [activePanel,setActivePanel]=useState<EditorPanel>('feed');
  const [zoom,setZoom]=useState(50);
  const [tplId,setTplId]=useState(feedInfo.template||'minimal-dark');
  const [fmtId,setFmtId]=useState('sq');
  const [bgColor,setBgColor]=useState('#0f0f0f');
  const [layers,setLayers]=useState<Layer[]>([]);
  const [selectedId,setSelectedId]=useState<string|null>(null);
  const [product,setProduct]=useState<Product|null>(null);
  const [selectedProductIdx,setSelectedProductIdx]=useState(-1);
  const [saving,setSaving]=useState(false);
  const [saved,setSaved]=useState(false);
  const [rightPanelOpen,setRightPanelOpen]=useState(false);
  const [proxyUrl,setProxyUrl]=useState<string>(feedInfo.proxy_url||'');
  const [proxyCopied,setProxyCopied]=useState(false);
  const [showSaveModal,setShowSaveModal]=useState(false);
  const [designName,setDesignName]=useState<string>(feedInfo.name||'');

  const fmt=FORMATS.find(f=>f.id===fmtId)||FORMATS[0];
  const canvasW=fmt.w, canvasH=fmt.h;
  const scale=zoom/100;
  const selectedLayer=layers.find(l=>l.id===selectedId)||null;

  // ilk ürünü yükle
  useEffect(()=>{
    const h:Record<string,string>={};
    const tok=localStorage.getItem('poyais_token');
    if(tok) h['Authorization']=`Bearer ${tok}`;
    // Önce bu feed'in products'ını dene, yoksa tüm feed'lerden src_url eşleşeni bul
    const feedId=feedInfo.feed_id||feedInfo.id;
    const tryFeedId=(fid:string)=>fetch(`${API}/feeds/${fid}/products?limit=1`,{headers:h}).then(r=>r.ok?r.json():{products:[],total:0});
    tryFeedId(feedId).then(d=>{
      if(d.products?.[0]){setProduct(d.products[0]);setSelectedProductIdx(0);}
      else if(d.total===0){
        // Yeni feed — src_url'si olan bir feed'i bul
        fetch(`${API}/feeds`,{headers:h}).then(r=>r.ok?r.json():[]).then((feeds:any[])=>{
          const src=feedInfo.src_url;
          const match=src?feeds.find((f:any)=>f.src_url===src&&f.item_count>0):feeds.find((f:any)=>f.item_count>0||(f.id!==feedInfo.id));
          if(match) tryFeedId(String(match.id)).then(d2=>{if(d2.products?.[0]){setProduct(d2.products[0]);setSelectedProductIdx(0);}}).catch(()=>{});
        }).catch(()=>{});
      }
    }).catch(()=>{});
  },[feedInfo.id]);

  // ürün değişince layer URL'lerini güncelle
  useEffect(()=>{
    if(!product) return;
    setLayers(prev=>prev.map(l=>{
      const val=getFieldValue(l.field,product);
      if(l.type==='image'&&val) return {...l,url:val};
      if(l.type==='text'&&!l.text) return {...l,text:val};
      return l;
    }));
  },[product]);

  const handleSelectProduct=(idx:number,p:Product)=>{
    setProduct(p);setSelectedProductIdx(idx);
    // layer URL'lerini yeni ürünle güncelle
    setLayers(prev=>prev.map((l:Layer)=>{
      const val=getFieldValue(l.field,p);
      if(l.type==='image'&&val) return {...l,url:val};
      if(l.type==='text') return {...l,text:val};
      return l;
    }));
  };

  const updateLayer=(updated:Layer)=>setLayers(prev=>prev.map(l=>l.id===updated.id?updated:l));
  const deleteSelected=()=>{setLayers(prev=>prev.filter(l=>l.id!==selectedId));setSelectedId(null);setRightPanelOpen(false);};

  const handleSelect=(id:string|null)=>{
    setSelectedId(id);
    if(id) setRightPanelOpen(true);
    // id null olunca panel KAPANMAZ — kullanıcı × ile kapatır
  };

  const handleSave=async()=>{
    setSaving(true);
    try{
      const tok=localStorage.getItem('poyais_token');
      await fetch(`${API}/feeds/${feedInfo.id}`,{method:'PUT',headers:{'Content-Type':'application/json','Authorization':`Bearer ${tok}`},body:JSON.stringify({template_id:tplId,name:feedInfo.name})});
    }catch{}
    setSaved(true);setSaving(false);setTimeout(()=>setSaved(false),2500);
    if(onUpdate) onUpdate({name:designName,template:tplId,proxy_url:proxyUrl});
    if(proxyUrl) setShowSaveModal(true);
  };


  return(
    <div style={{display:'flex',flexDirection:'column' as const,height:'calc(100vh - 112px)',background:BG}}>
      {showSaveModal&&proxyUrl&&<SaveSuccessModal proxyUrl={proxyUrl} onClose={()=>setShowSaveModal(false)}/>}
      {/* Topbar */}
      <div style={{display:'flex',alignItems:'center',justifyContent:'space-between',padding:'9px 16px',background:S1,borderBottom:`1px solid ${BD}`,flexShrink:0}}>
        <div style={{display:'flex',alignItems:'center',gap:8,fontSize:12}}>
          <span style={{color:G,cursor:'pointer',fontWeight:600}} onClick={onBack}>← Feed Wizard</span>
          <span style={{color:BD}}>›</span>
          <input
            value={designName}
            onChange={(e:any)=>setDesignName(e.target.value)}
            style={{background:'transparent',border:'none',borderBottom:`1px solid ${BD}`,outline:'none',color:TX,fontWeight:600,fontSize:12,padding:'2px 4px',minWidth:80,maxWidth:200}}
            onBlur={(e:any)=>{if(!e.target.value.trim()) setDesignName(feedInfo.name);}}
          />
          <span style={{fontSize:10,background:S3,border:`1px solid ${BD}`,borderRadius:4,padding:'2px 8px',color:MT}}>{TEMPLATES.find(tp=>tp.id===tplId)?.name||tplId}</span>
          <span style={{fontSize:10,background:S3,border:`1px solid ${BD}`,borderRadius:4,padding:'2px 8px',color:MT}}>{fmt.label} {fmt.w}×{fmt.h}</span>
        </div>
        <div style={{display:'flex',gap:8,alignItems:'center'}}>
          <div style={{display:'flex',alignItems:'center',gap:5,background:S2,border:`1px solid ${BD}`,borderRadius:6,padding:'3px 10px'}}>
            <button onClick={()=>setZoom(z=>Math.max(10,z-10))} style={{background:'none',border:'none',cursor:'pointer',color:MT,fontSize:16,lineHeight:1,padding:0}}>−</button>
            <span style={{fontSize:11,color:MT,minWidth:34,textAlign:'center' as const,fontFamily:MONO}}>{zoom}%</span>
            <button onClick={()=>setZoom(z=>Math.min(200,z+10))} style={{background:'none',border:'none',cursor:'pointer',color:MT,fontSize:16,lineHeight:1,padding:0}}>+</button>
          </div>
          <Btn small onClick={()=>{if(selectedLayer) deleteSelected();}}>↩</Btn>
          <Btn primary onClick={handleSave} disabled={saving} style={saved?{background:GR,color:'#fff',border:`1px solid ${GR}`}:{}}>
            {saving?t('Kaydediliyor...'):saved?t('✓ Kaydedildi'):t('Kaydet')}
          </Btn>
        </div>
      </div>

      {/* Body */}
      <div style={{display:'flex',flex:1,overflow:'hidden'}}>
        {/* Sidebar icons */}
        <div style={{width:62,background:S1,borderRight:`1px solid ${BD}`,display:'flex',flexDirection:'column' as const,alignItems:'center',paddingTop:4,flexShrink:0,overflowY:'auto' as const}}>
          {(Object.keys(PANEL_LABELS) as EditorPanel[]).map(id=>(
            <button key={id} onClick={()=>setActivePanel(id)}
              style={{background:'none',border:'none',borderLeft:`3px solid ${activePanel===id?G:'transparent'}`,width:'100%',padding:'9px 0',cursor:'pointer',display:'flex',flexDirection:'column' as const,alignItems:'center',gap:2,backgroundColor:activePanel===id?'rgba(201,168,76,0.06)':'transparent',transition:'all 0.1s'}}>
              <span style={{fontSize:14,color:activePanel===id?G:'#aaa'}}>{PANEL_ICONS[id]}</span>
              <span style={{fontSize:7,color:activePanel===id?G:'#bbb',letterSpacing:'0.02em',textTransform:'uppercase' as const,textAlign:'center' as const,lineHeight:1.2,padding:'0 3px'}}>{t(PANEL_LABELS[id])}</span>
            </button>
          ))}
        </div>

        {/* Left panel */}
        <div style={{width:260,background:S1,borderRight:`1px solid ${BD}`,overflowY:'auto' as const,flexShrink:0}}>
          <div style={{fontSize:11,fontWeight:700,color:MT,textTransform:'uppercase' as const,letterSpacing:'0.1em',padding:'11px 14px 9px',borderBottom:`1px solid ${BD}`}}>
            {t(PANEL_LABELS[activePanel])}
          </div>
          <div style={{padding:12}}>
            {activePanel==='feed'&&<FeedPanel feedInfo={feedInfo} product={product} layers={layers} setLayers={setLayers} selectedId={selectedId} setSelectedId={handleSelect} canvasW={canvasW} canvasH={canvasH}/>}
            {activePanel==='products'&&<ProductsPanel feedId={feedInfo.id} onSelect={handleSelectProduct} selectedIdx={selectedProductIdx}/>}
            {activePanel==='templates'&&<TemplatesPanel current={tplId} onChange={setTplId} product={product} setLayers={setLayers} canvasW={canvasW} canvasH={canvasH}/>}
            {activePanel==='text'&&<TextPanel setLayers={setLayers} setSelectedId={setSelectedId} canvasW={canvasW} canvasH={canvasH} setRightPanelOpen={setRightPanelOpen}/>}
            {activePanel==='photos'&&<PhotosPanel setLayers={setLayers} setSelectedId={setSelectedId} setRightPanelOpen={setRightPanelOpen} canvasW={canvasW} canvasH={canvasH}/>}
            {activePanel==='elements'&&<ElementsPanel setLayers={setLayers} setSelectedId={setSelectedId} setRightPanelOpen={setRightPanelOpen} canvasW={canvasW} canvasH={canvasH}/>}
            {activePanel==='assets'&&<AssetsPanel layers={layers} selectedId={selectedId} setLayers={setLayers} setSelectedId={setSelectedId} setRightPanelOpen={setRightPanelOpen} canvasW={canvasW} canvasH={canvasH}/>}
            {activePanel==='background'&&<BackgroundPanel bgColor={bgColor} setBgColor={setBgColor}/>}
            {activePanel==='layers'&&<LayersListPanel layers={layers} selectedId={selectedId} setSelectedId={handleSelect} setLayers={setLayers}/>}
            {activePanel==='resize'&&<ResizePanel fmtId={fmtId} setFmtId={setFmtId}/>}
          </div>
        </div>

        {/* Canvas */}
        <div style={{flex:1,background:'#DCDDE2',display:'flex',flexDirection:'column' as const,alignItems:'center',overflow:'auto' as const,padding:'14px 0 40px'}}>
          {/* Canvas toolbar */}
          <div style={{position:'sticky' as const,top:0,zIndex:10,display:'flex',gap:6,background:S1,border:`1px solid ${BD}`,borderRadius:8,padding:'6px 14px',marginBottom:16,alignItems:'center',flexShrink:0}}>
            <button onClick={()=>{setLayers([]);setSelectedId(null);}} title={t('Temizle')} style={{background:'none',border:'none',cursor:'pointer',color:MT,fontSize:13,padding:'2px 6px'}}>↩</button>
            <div style={{width:1,height:16,background:BD}}/>
            {selectedLayer?(
              <>
                <span style={{fontSize:11,color:TX,fontWeight:600}}>{selectedLayer.field}</span>
                <span style={{fontSize:10,color:MT}}>{Math.round(selectedLayer.w)}×{Math.round(selectedLayer.h)}px</span>
                <div style={{width:1,height:16,background:BD}}/>
                <button onClick={deleteSelected} style={{background:'none',border:'none',cursor:'pointer',color:RD,fontSize:13,padding:'2px 6px'}}>{t('🗑 Sil')}</button>
              </>
            ):(
              <span style={{fontSize:11,color:MT}}>{t("Feed panelinden alan ekle veya canvas'ta seç.")}</span>
            )}
          </div>

          {/* Scaled canvas */}
          <div style={{transform:`scale(${scale})`,transformOrigin:'top center',marginBottom:Math.max(0,canvasH*(scale-1)+40)}}>
            <Canvas layers={layers} selectedId={selectedId} onSelect={handleSelect} onUpdate={updateLayer} canvasW={canvasW} canvasH={canvasH} bgColor={bgColor} scale={scale}/>
          </div>
        </div>

        {/* Right panel — layer properties, stays open once opened */}
        {rightPanelOpen&&(
          <div style={{width:220,background:S1,borderLeft:`1px solid ${BD}`,overflowY:'auto' as const,flexShrink:0,display:'flex',flexDirection:'column' as const}}>
            <div style={{display:'flex',alignItems:'center',justifyContent:'space-between',padding:'11px 14px 9px',borderBottom:`1px solid ${BD}`,flexShrink:0}}>
              <span style={{fontSize:11,fontWeight:700,color:MT,textTransform:'uppercase' as const,letterSpacing:'0.1em'}}>{t('Özellikler')}</span>
              <button onClick={()=>{setRightPanelOpen(false);setSelectedId(null);}} style={{background:'none',border:'none',cursor:'pointer',color:MT,fontSize:16,lineHeight:1,padding:0}}>×</button>
            </div>
            <div style={{padding:12,flex:1,overflowY:'auto' as const}}>
              {selectedLayer
                ? <LayerPropsPanel layer={selectedLayer} onUpdate={updateLayer} onDelete={deleteSelected}/>
                : <p style={{fontSize:12,color:MT}}>{t("Canvas'ta bir öğeye tıklayın.")}</p>
              }
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

// ── WIZARD MODAL ───────────────────────────────────────────────────────────────
function CreateWizardModal({onClose,catalogs,feedsList,onOpenEditor,previewProduct}:any){
  const { t } = useLang();
  const [step,setStep]=useState<WizardStep>(1);
  const [selectedTemplate,setSelectedTemplate]=useState('minimal-dark');
  const [catalog,setCatalog]=useState(catalogs[0]||'');
  const [designName,setDesignName]=useState('');
  const [saving,setSaving]=useState(false);

  const go=useCallback(async()=>{
    if(!designName) return;
    setSaving(true);
    // Yeni feed oluşturma — mevcut feed'i kullan, sadece template değiştir
    const matchedFeed=(feedsList||[]).find((f:any)=>f.name===catalog||f.catalog===catalog)||(feedsList||[])[0];
    let fid=matchedFeed?.id||Date.now().toString();
    try{
      const tok=localStorage.getItem('poyais_token');
      // Mevcut feed varsa template'i güncelle, yoksa yeni oluştur
      // Her zaman /designs'a yeni tasarım ekle
      const r=await fetch(`${API}/designs`,{
        method:'POST',
        headers:{'Content-Type':'application/json','Authorization':`Bearer ${tok}`},
        body:JSON.stringify({
          feed_id:matchedFeed?.id||1,
          name:designName,
          template_id:selectedTemplate,
        }),
      });
      if(r.ok){const d=await r.json();fid=String(d.id||fid);}
    }catch{}
    setSaving(false);onClose();
    onOpenEditor({
      id:fid,
      name:designName,
      catalog:matchedFeed?.catalog||matchedFeed?.name||catalog,
      template:selectedTemplate,
      status:'draft',
      products:matchedFeed?.products||matchedFeed?.cached||0,
      cached:matchedFeed?.cached||matchedFeed?.products||0,
      createdAt:new Date().toISOString().slice(0,10),
      src_url:matchedFeed?.src_url||'',
      proxy_url:matchedFeed?.proxy_url||'',
    });
  },[designName,catalog,selectedTemplate,feedsList,onClose,onOpenEditor]);

  return(
    <div style={{position:'fixed' as const,inset:0,zIndex:1000,background:'rgba(0,0,0,0.4)',display:'flex',alignItems:'center',justifyContent:'center',backdropFilter:'blur(4px)'}}>
      <div style={{background:S1,border:`1px solid ${BD}`,borderRadius:radius.panel,width:'min(90vw,660px)',maxHeight:'92vh',overflowY:'auto' as const,padding:36,boxShadow:shadow.pop,position:'relative' as const}}>
        <button onClick={onClose} style={{position:'absolute' as const,top:16,right:16,background:'none',border:'none',color:MT,cursor:'pointer',fontSize:20,lineHeight:1}}>×</button>
        <div style={{marginBottom:24}}>
          <h2 style={{margin:'0 0 4px',fontSize:18,color:TX,fontWeight:700}}>{t('Tasarım Oluştur')}</h2>
          <p style={{margin:0,fontSize:12,color:MT}}>{t('Feed tabanlı dinamik görsel üreticisi')}</p>
        </div>
        <StepIndicator step={step}/>
        {step===1&&(
          <div>
            <div style={{marginBottom:28}}>
              <Label>{t('Şablon Seç')}</Label>
              <div style={{display:'grid',gridTemplateColumns:'repeat(3,1fr)',gap:10}}>
                {TEMPLATES.slice(0,9).map(tpl=>(
                  <div key={tpl.id} onClick={()=>setSelectedTemplate(tpl.id)} style={{cursor:'pointer',borderRadius:10,overflow:'hidden',border:`2px solid ${selectedTemplate===tpl.id?G:BD}`,background:S1,boxShadow:selectedTemplate===tpl.id?`0 0 16px rgba(201,168,76,0.2)`:'none',transition:'all 0.15s',transform:selectedTemplate===tpl.id?'scale(1.03)':'scale(1)'}}>
                    <div style={{height:90,overflow:'hidden',position:'relative' as const}}>
                      {previewProduct
                        ?<img src={`${API}/render?src=${encodeURIComponent(previewProduct.img)}&tpl=${tpl.id}&fmt=sq&title=${encodeURIComponent(previewProduct.title)}&price=${encodeURIComponent(previewProduct.price)}&brand=${encodeURIComponent(previewProduct.brand)}&badge=${encodeURIComponent(previewProduct.badge||'')}`} alt="" style={{width:'100%',height:'100%',objectFit:'cover' as const}} loading="lazy"
                            onError={(e:any)=>{
                              const bg=({'minimal-dark':'#0f0f0f','clean-white':'#f5f5f5','gradient-luxury':'#2A1F0A','bold-promo':'#300000','fashion-dark':'#111111','fashion-light':'#fafafa','tech-blue':'#050d1a','food-warm':'#1c0f00','seasonal-dark':'#0d1f0d','glass-premium':'#1a1a2e'} as any)[tpl.id]||'#111';
                              const accent=({'minimal-dark':'#C9A84C','clean-white':'#111111','gradient-luxury':'#C9A84C','bold-promo':'#E05252','fashion-dark':'#C9A84C','fashion-light':'#1a1a1a','tech-blue':'#4d9fff','food-warm':'#e8740a','seasonal-dark':'#4caf50','glass-premium':'#a8d8ff'} as any)[tpl.id]||'#C9A84C';
                              e.currentTarget.style.display='none';
                              const p=e.currentTarget.parentElement;
                              if(p){p.style.background=bg;p.innerHTML=`<div style="width:100%;height:100%;display:flex;flex-direction:column;align-items:center;justify-content:center;gap:6px;padding:8px"><div style="width:60%;height:4px;border-radius:2px;background:${accent}"></div><div style="width:40%;height:3px;border-radius:2px;background:rgba(255,255,255,0.15)"></div><div style="width:50%;height:8px;border-radius:2px;background:rgba(255,255,255,0.06)"></div></div><div style="position:absolute;top:6px;right:6px;width:16px;height:16px;border-radius:50%;background:${selectedTemplate===tpl.id?G:'transparent'};display:flex;align-items:center;justify-content:center;font-size:9px;color:#1a1200;font-weight:900">${selectedTemplate===tpl.id?'✓':''}</div>`;}
                            }}
                          />
                        :<div style={{width:'100%',height:'100%',background:({'minimal-dark':'#0f0f0f','clean-white':'#f5f5f5','gradient-luxury':'#2A1F0A','bold-promo':'#300000','fashion-dark':'#111111','fashion-light':'#fafafa','tech-blue':'#050d1a','food-warm':'#1c0f00','seasonal-dark':'#0d1f0d','glass-premium':'#1a1a2e'} as any)[tpl.id]||'#111',display:'flex',flexDirection:'column' as const,alignItems:'center',justifyContent:'center',gap:6,padding:8}}>
                            <div style={{width:'60%',height:4,borderRadius:2,background:({'minimal-dark':'#C9A84C','clean-white':'#111111','gradient-luxury':'#C9A84C','bold-promo':'#E05252','fashion-dark':'#C9A84C','fashion-light':'#1a1a1a','tech-blue':'#4d9fff','food-warm':'#e8740a','seasonal-dark':'#4caf50','glass-premium':'#a8d8ff'} as any)[tpl.id]||'#C9A84C'}}/>
                            <div style={{width:'40%',height:3,borderRadius:2,background:'rgba(255,255,255,0.15)'}}/>
                            <div style={{width:'50%',height:8,borderRadius:2,background:'rgba(255,255,255,0.06)'}}/>
                          </div>
                      }
                      {selectedTemplate===tpl.id&&<div style={{position:'absolute' as const,top:6,right:6,width:16,height:16,borderRadius:'50%',background:G,display:'flex',alignItems:'center',justifyContent:'center',fontSize:9,color:'#1a1200',fontWeight:900}}>✓</div>}
                    </div>
                    <div style={{padding:'6px 10px',borderTop:`1px solid ${BD}`}}><div style={{fontSize:11,color:selectedTemplate===tpl.id?G:TX,fontWeight:selectedTemplate===tpl.id?700:400}}>{tpl.name}</div></div>
                  </div>
                ))}
              </div>
            </div>
            <div style={{display:'flex',justifyContent:'flex-end',marginTop:16}}><Button variant="solid" color={T.accent} onClick={()=>{if(!selectedTemplate){setSelectedTemplate('minimal-dark');}setStep(2);}}>{t('İleri →')}</Button></div>
          </div>
        )}
        {step===2&&(
          <div>
            <div style={{display:'grid',gridTemplateColumns:'1fr 1fr',gap:16,marginBottom:20}}>
              <div><Label>{t('Katalog')}</Label><Sel value={catalog} onChange={setCatalog} options={catalogs.length>0?catalogs:[t('Yükleniyor...')]}/></div>
              <div><Label>{t('Tasarım Adı')}</Label><Inp value={designName} onChange={setDesignName} placeholder={t('Tasarım adını girin...')}/></div>
            </div>
            <div style={{display:'flex',justifyContent:'space-between',marginTop:28}}>
              <Button variant="ghost" onClick={()=>setStep(1)}>{t('← Geri')}</Button>
              <Button variant="solid" color={T.accent} disabled={!designName||saving} onClick={go}>{saving?t('Oluşturuluyor...'):t('Editöre Geç →')}</Button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

// ── StepIndicator ──────────────────────────────────────────────────────────────
function StepIndicator({step}:{step:WizardStep}){
  const { t } = useLang();
  const steps=[{n:1 as WizardStep,label:t('Şablon Seç')},{n:2 as WizardStep,label:t('Katalog')}];
  return(
    <div style={{display:'flex',alignItems:'center',marginBottom:28}}>
      {steps.map((s,i)=>{
        const done=s.n<step,active=s.n===step;
        return(
          <div key={s.n} style={{display:'flex',alignItems:'center',flex:i<steps.length-1?1:0}}>
            <div style={{display:'flex',alignItems:'center',gap:8}}>
              <div style={{width:28,height:28,borderRadius:'50%',background:done||active?`linear-gradient(135deg,${G},${GL})`:S3,border:`1px solid ${done||active?G:BD}`,display:'flex',alignItems:'center',justifyContent:'center',fontSize:11,fontWeight:800,color:done||active?'#1a1200':MT,flexShrink:0}}>
                {done?'✓':s.n}
              </div>
              <span style={{fontSize:11,color:active?TX:MT,fontWeight:active?700:400,whiteSpace:'nowrap' as const}}>{s.label}</span>
            </div>
            {i<steps.length-1&&<div style={{flex:1,height:1,margin:'0 12px',background:done?G:BD}}/>}
          </div>
        );
      })}
    </div>
  );
}

// ── STATUS BADGE ───────────────────────────────────────────────────────────────
function StatusBadge({status}:{status:Feed['status']}){
  const { t } = useLang();
  const m={active:{label:'Aktif',color:T.greenText},draft:{label:'Taslak',color:T.accent},generating:{label:'Üretiliyor',color:T.amberText}};
  const {label,color}=m[status];
  return <Badge color={color}>{t(label)}</Badge>;
}

// ── AYARLAR (FEED REGISTRY) ─────────────────────────────────────────────────────
export function FeedRegistrySettings({feedRegistry,clients,onChange}:{feedRegistry:any[];clients:any[];onChange:()=>void}){
  const { t } = useLang();
  const [showAdd,setShowAdd]=useState(false);
  const [saving,setSaving]=useState(false);
  const [error,setError]=useState('');
  const [form,setForm]=useState({name:'',src_url:'',feed_type:'google',client_id:''});
  const [editingId,setEditingId]=useState<string|null>(null);
  const [editForm,setEditForm]=useState({name:'',src_url:''});
  const [editSaving,setEditSaving]=useState(false);
  const [editError,setEditError]=useState('');

  const tok=()=>localStorage.getItem('poyais_token');
  const authH=()=>({'Content-Type':'application/json',Authorization:`Bearer ${tok()}`});

  const addFeed=async()=>{
    if(!form.name.trim()||!form.src_url.trim()){setError(t('Ad ve URL zorunludur.'));return;}
    setSaving(true);setError('');
    try{
      const r=await fetch(`${API}/feeds`,{method:'POST',headers:authH(),
        body:JSON.stringify({name:form.name,src_url:form.src_url,feed_type:form.feed_type,client_id:form.client_id,template_id:'minimal-dark',formats:['sq']})});
      if(!r.ok)throw new Error();
      setShowAdd(false);setForm({name:'',src_url:'',feed_type:'google',client_id:''});
      onChange();
    }catch{setError(t('Feed eklenirken hata oluştu.'));}
    setSaving(false);
  };

  const assignClient=async(feedId:string,clientId:string)=>{
    try{
      await fetch(`${API}/feeds/${feedId}/assign`,{method:'POST',headers:authH(),body:JSON.stringify({client_id:clientId})});
      onChange();
    }catch{}
  };

  const removeFeed=async(feedId:string)=>{
    if(!window.confirm(t('Bu feed bağlantısını silmek istediğinize emin misiniz? Buna bağlı tasarımlar etkilenebilir.')))return;
    try{
      await fetch(`${API}/feeds/${feedId}`,{method:'DELETE',headers:authH()});
      onChange();
    }catch{}
  };

  const startEdit=(f:any)=>{ setEditingId(String(f.id)); setEditForm({name:f.name||'',src_url:f.src_url||''}); setEditError(''); };
  const cancelEdit=()=>{ setEditingId(null); setEditError(''); };
  const saveEdit=async(feedId:string)=>{
    if(!editForm.name.trim()||!editForm.src_url.trim()){setEditError(t('Ad ve URL zorunludur.'));return;}
    setEditSaving(true);setEditError('');
    try{
      const r=await fetch(`${API}/feeds/${feedId}`,{method:'PUT',headers:authH(),
        body:JSON.stringify({name:editForm.name,src_url:editForm.src_url})});
      if(!r.ok)throw new Error();
      setEditingId(null);
      onChange();
    }catch{setEditError(t('Kaydedilirken hata oluştu.'));}
    setEditSaving(false);
  };

  return(
    <div>
      <Card pad={0} style={{overflow:'hidden',marginBottom:20}}>
        <div style={{padding:'14px 18px',borderBottom:`1px solid ${BD}`,display:'flex',alignItems:'center',justifyContent:'space-between'}}>
          <SectionHeader title={t("Bağlı Feed'ler")}/>
          <Button variant="solid" color={T.accent} onClick={()=>setShowAdd(v=>!v)}>{t('Feed Ekle')}</Button>
        </div>
        {showAdd&&(
          <div style={{padding:18,borderBottom:`1px solid ${BD}`,background:S2}}>
            <div style={{display:'grid',gridTemplateColumns:'1fr 1fr',gap:10,marginBottom:10}}>
              <input value={form.name} onChange={e=>setForm(f=>({...f,name:e.target.value}))} placeholder={t('Feed adı')}
                style={{boxSizing:'border-box' as const,background:S1,border:`1px solid ${BD}`,borderRadius:8,padding:'9px 12px',color:TX,fontSize:12.5,outline:'none'}}/>
              <select value={form.feed_type} onChange={e=>setForm(f=>({...f,feed_type:e.target.value}))}
                style={{boxSizing:'border-box' as const,background:S1,border:`1px solid ${BD}`,borderRadius:8,padding:'9px 12px',color:TX,fontSize:12.5,outline:'none'}}>
                <option value="google">Google Shopping</option>
                <option value="meta">{t('Meta Katalog')}</option>
              </select>
            </div>
            <input value={form.src_url} onChange={e=>setForm(f=>({...f,src_url:e.target.value}))} placeholder="Feed URL (https://...)"
              style={{boxSizing:'border-box' as const,width:'100%',background:S1,border:`1px solid ${BD}`,borderRadius:8,padding:'9px 12px',color:TX,fontSize:12.5,outline:'none',marginBottom:10}}/>
            <select value={form.client_id} onChange={e=>setForm(f=>({...f,client_id:e.target.value}))}
              style={{boxSizing:'border-box' as const,width:'100%',background:S1,border:`1px solid ${BD}`,borderRadius:8,padding:'9px 12px',color:TX,fontSize:12.5,outline:'none',marginBottom:12}}>
              <option value="">{t('Müşteriye bağlama (isteğe bağlı)')}</option>
              {clients.map((c:any)=><option key={c.id} value={c.id}>{c.name}</option>)}
            </select>
            {error&&<div style={{color:RD,fontSize:11.5,marginBottom:10}}>{error}</div>}
            <div style={{display:'flex',gap:8}}>
              <Btn small onClick={addFeed}>{saving?t('Ekleniyor...'):t('Kaydet')}</Btn>
              <button onClick={()=>{setShowAdd(false);setError('');}} style={{padding:'7px 14px',borderRadius:6,fontSize:11.5,fontWeight:700,cursor:'pointer',background:'transparent',border:`1px solid ${BD}`,color:MT}}>{t('İptal')}</button>
            </div>
          </div>
        )}
        {feedRegistry.length===0?(
          <div style={{padding:'40px 18px',textAlign:'center' as const,fontSize:13,color:MT}}>{t('Henüz bağlı feed yok. Yukarıdan bir feed ekleyin.')}</div>
        ):feedRegistry.map((f:any,i:number)=>(
          editingId===String(f.id)?(
            <div key={f.id} style={{padding:'14px 18px',borderBottom:i<feedRegistry.length-1?`1px solid ${S3}`:'none',background:S2}}>
              <div style={{display:'grid',gridTemplateColumns:'1fr 2fr',gap:10,marginBottom:8}}>
                <div>
                  <div style={{fontSize:10,fontWeight:700,color:MT,marginBottom:4}}>{t('Feed Adı')}</div>
                  <input value={editForm.name} onChange={e=>setEditForm(v=>({...v,name:e.target.value}))}
                    style={{boxSizing:'border-box' as const,width:'100%',background:S1,border:`1px solid ${BD}`,borderRadius:8,padding:'8px 10px',color:TX,fontSize:12.5,outline:'none'}}/>
                </div>
                <div>
                  <div style={{fontSize:10,fontWeight:700,color:MT,marginBottom:4}}>Feed URL</div>
                  <input value={editForm.src_url} onChange={e=>setEditForm(v=>({...v,src_url:e.target.value}))}
                    style={{boxSizing:'border-box' as const,width:'100%',background:S1,border:`1px solid ${BD}`,borderRadius:8,padding:'8px 10px',color:TX,fontSize:12.5,outline:'none'}}/>
                </div>
              </div>
              {editError&&<div style={{color:RD,fontSize:11.5,marginBottom:8}}>{editError}</div>}
              <div style={{display:'flex',gap:8}}>
                <Btn small onClick={()=>saveEdit(String(f.id))}>{editSaving?t('Kaydediliyor...'):t('Kaydet')}</Btn>
                <button onClick={cancelEdit} style={{padding:'7px 14px',borderRadius:6,fontSize:11.5,fontWeight:700,cursor:'pointer',background:'transparent',border:`1px solid ${BD}`,color:MT}}>{t('İptal')}</button>
              </div>
            </div>
          ):(
          <div key={f.id} style={{display:'grid',gridTemplateColumns:'1.5fr 2fr 1.6fr',padding:'14px 18px',borderBottom:i<feedRegistry.length-1?`1px solid ${S3}`:'none',alignItems:'center',gap:10}}>
            <div style={{fontSize:13,color:TX,fontWeight:600}}>{f.name}</div>
            <div style={{fontSize:11,color:MT,overflow:'hidden',textOverflow:'ellipsis',whiteSpace:'nowrap' as const}}>{f.src_url}</div>
            <div style={{display:'flex',gap:6,alignItems:'center'}}>
              <select value={f.client_id||''} onChange={e=>assignClient(String(f.id),e.target.value)}
                style={{flex:1,background:S2,border:`1px solid ${BD}`,borderRadius:6,padding:'6px 8px',color:TX,fontSize:11.5,outline:'none'}}>
                <option value="">{t('Atanmamış')}</option>
                {clients.map((c:any)=><option key={c.id} value={c.id}>{c.name}</option>)}
              </select>
              <button onClick={()=>startEdit(f)} style={{padding:'6px 10px',borderRadius:6,fontSize:11,fontWeight:700,cursor:'pointer',background:S2,border:`1px solid ${BD}`,color:TX}}>{t('Düzenle')}</button>
              <button onClick={()=>removeFeed(String(f.id))} style={{padding:'6px 10px',borderRadius:6,fontSize:11,fontWeight:700,cursor:'pointer',background:'rgba(224,82,82,0.08)',border:`1px solid ${RD}`,color:RD}}>{t('Sil')}</button>
            </div>
          </div>
          )
        ))}
      </Card>
    </div>
  );
}

// ── MAIN TAB ───────────────────────────────────────────────────────────────────
export default function FeedWizardTab({clientId}:{clientId?:number}={}){
  const { t } = useLang();
  const [feeds,setFeeds]=useState<Feed[]>([]);
  const [catalogs,setCatalogs]=useState<string[]>([]);
  const [feedRegistry,setFeedRegistry]=useState<any[]>([]);
  const [activeTab,setActiveTab]=useState<'designs'|'settings'>('designs');
  const [wizardOpen,setWizardOpen]=useState(false);
  const [editorFeed,setEditorFeed]=useState<Feed|null>(null);
  const [search,setSearch]=useState('');
  const [copied,setCopied]=useState<string|null>(null);
  const [loading,setLoading]=useState(true);
  const [previewProduct,setPreviewProduct]=useState<Product|null>(null);
  const [totalProducts,setTotalProducts]=useState(0);

  useEffect(()=>{
    const h:Record<string,string>={};
    const tok=localStorage.getItem('poyais_token');
    if(tok) h['Authorization']=`Bearer ${tok}`;
    // client_id her zaman gönderiliyor — eskiden hiç gönderilmiyordu, backend "hiç
    // client_id verilmemiş" dalına düşüp kullanıcının erişebildiği TÜM müşterilerin
    // feed'lerini tek bir düz liste olarak dönüyordu, aktif müşteriye göre hiç
    // filtrelenmiyordu. Kullanıcı canlıda yakaladı (2026-09-13): Denim Trip'teyken
    // ZsaZsaZsu'nun feed'ini görebiliyordu.
    const feedsQs = clientId ? `?client_id=${clientId}` : '';
    // Önce feedleri çek (katalog listesi için)
    fetch(`${API}/feeds${feedsQs}`,{headers:h})
      .then(r=>r.ok?r.json():[])
      .then((fdata:any[])=>{
        if(!Array.isArray(fdata)) return;
        const cats=Array.from(new Set(fdata.map((d:any)=>d.name||'Katalog'))) as string[];
        setCatalogs(cats);
        setFeedRegistry(fdata);
        const feedMap:Record<string,any>={};
        fdata.forEach((d:any)=>{feedMap[String(d.id)]=d;});
        (window as any).__poyaisFeeds=fdata;
        // Preview ürünü çek
        if(fdata.length>0){
          fetch(`${API}/feeds/${fdata[0].id}/products?limit=1`,{headers:h})
            .then(r=>r.ok?r.json():{products:[]})
            .then((pd:any)=>{if(pd.products?.[0]) setPreviewProduct(pd.products[0]);})
            .catch(()=>{});
        }
        // Şimdi tasarımları çek
        fetch(`${API}/designs${clientId ? `?client_id=${clientId}` : ''}`,{headers:h})
          .then(r=>r.ok?r.json():[])
          .then((data:any[])=>{
            if(!Array.isArray(data)){setLoading(false);return;}
            const mapped:Feed[]=data.map((d:any)=>({
              id:String(d.id),
              name:d.name||'İsimsiz',
              catalog:d.feed_name||'Katalog',
              template:d.template_id||'minimal-dark',
              status:(d.proxy_url&&d.src_url?'active':'draft') as Feed['status'],
              products:0,cached:0,
              createdAt:d.created_at?new Date(d.created_at*1000).toISOString().slice(0,10):'',
              src_url:d.src_url||'',
              proxy_url:d.proxy_url||'',
              feed_id:String(d.feed_id||''),
              canvas_data:d.canvas_data,
              bg_color:d.bg_color,
            }));
            setFeeds(mapped);
            setLoading(false);
            // Ürün sayılarını çek
            let total=0;
            // Unique feed_id'leri bul ve her biri için bir kez fetch yap
            const uniqueFeedIds=Array.from(new Set(data.map((d:any)=>String(d.feed_id))));
            const feedProductMap:Record<string,number>={};
            Promise.all(uniqueFeedIds.map((fid:string)=>
              fetch(`${API}/feeds/${fid}/products?limit=1`,{headers:h})
                .then(r=>r.ok?r.json():{total:0})
                .then((pd:any)=>{feedProductMap[fid]=pd.total||0;})
                .catch(()=>{})
            )).then(()=>{
              let tot=0;
              setFeeds(prev=>prev.map(f=>{
                const cnt=feedProductMap[String((f as any).feed_id)]||0;
                tot+=cnt;
                return {...f,products:cnt,cached:cnt};
              }));
              setTotalProducts(tot);
            });
          }).catch(()=>setLoading(false));
      }).catch(()=>setLoading(false));
  },[clientId]);

  const [clients,setClients]=useState<any[]>([]);
  useEffect(()=>{
    const tok=localStorage.getItem('poyais_token');
    fetch(`/api/v1/clients/`,{headers:tok?{Authorization:`Bearer ${tok}`}:{}})
      .then(r=>r.ok?r.json():[]).then(d=>setClients(Array.isArray(d)?d:[])).catch(()=>{});
  },[]);
  const reloadFeedRegistry=useCallback(()=>{
    const tok=localStorage.getItem('poyais_token');
    const h:Record<string,string>=tok?{Authorization:`Bearer ${tok}`}:{};
    const qs = clientId ? `?client_id=${clientId}` : '';
    fetch(`${API}/feeds${qs}`,{headers:h}).then(r=>r.ok?r.json():[]).then((fdata:any[])=>{
      if(!Array.isArray(fdata))return;
      setFeedRegistry(fdata);
      setCatalogs(Array.from(new Set(fdata.map((d:any)=>d.name||'Katalog'))) as string[]);
    }).catch(()=>{});
  },[clientId]);

  const Inp2=({value,onChange,placeholder,style={}}:any)=>(
    <input value={value} onChange={(e:any)=>onChange(e.target.value)} placeholder={placeholder}
      style={{boxSizing:'border-box' as const,background:S2,border:`1px solid ${BD}`,borderRadius:6,padding:'7px 10px',color:TX,fontSize:12,outline:'none',...style}}/>
  );

  const filtered=feeds.filter(f=>f.name.toLowerCase().includes(search.toLowerCase()));
  if(editorFeed) return <Editor feedInfo={editorFeed} onBack={()=>setEditorFeed(null)} onUpdate={(upd:any)=>setFeeds(prev=>prev.map(f=>f.id===editorFeed?.id?{...f,...upd}:f))}/>;

  const hasFeed=feedRegistry.length>0;
  const openWizard=()=>{ if(!hasFeed){setActiveTab('settings');return;} setWizardOpen(true); };

  return(
    <div style={{padding:'0 0 40px'}}>
      <div style={{display:'flex',alignItems:'flex-start',justifyContent:'space-between',marginBottom:20,flexWrap:'wrap' as const,gap:12}}>
        <p style={{margin:0,fontSize:13,color:MT}}>{t('Katalog tabanlı dinamik feed görselleri oluşturun.')}</p>
        {activeTab==='designs'&&<Button variant="solid" color={T.accent} onClick={openWizard}>{t('Yeni Tasarım')}</Button>}
      </div>
      <div style={{display:'flex',gap:6,marginBottom:24,background:S2,borderRadius:10,padding:4,width:'fit-content'}}>
        {[{k:'designs',l:t('Tasarımlar')},{k:'settings',l:t('Ayarlar')}].map(tb=>(
          <button key={tb.k} onClick={()=>setActiveTab(tb.k as any)} style={{padding:'7px 16px',borderRadius:7,fontSize:12.5,fontWeight:700,cursor:'pointer',
            background:activeTab===tb.k?S1:'transparent',color:activeTab===tb.k?TX:MT,border:'none'}}>
            {tb.l}
          </button>
        ))}
      </div>
      {activeTab==='settings'?(
        <FeedRegistrySettings feedRegistry={feedRegistry} clients={clients} onChange={reloadFeedRegistry}/>
      ):(<>
      {!hasFeed&&(
        <div style={{marginBottom:20,padding:'14px 18px',borderRadius:12,background:'#FBF6E8',border:'1px solid #F0E2B4',fontSize:12.5,color:'#8A6212'}}>
          {t('Tasarım oluşturmadan önce Ayarlar sekmesinden bir feed bağlamanız gerekiyor.')}
        </div>
      )}
      <Grid min={160} gap={10} style={{marginBottom:28}}>
        {[{label:'Tasarım',value:feeds.length},{label:'Aktif',value:feeds.filter(f=>!!(f as any).proxy_url).length},{label:'Toplam Ürün',value:totalProducts>0?totalProducts.toLocaleString('tr'):feeds.reduce((a,f)=>a+(f.cached||f.products||0),0).toLocaleString('tr')},{label:'Katalog',value:Array.from(new Set(feeds.map(f=>f.catalog))).length}].map((s,i)=>(
          <Card key={i} pad={18}>
            <Stat label={t(s.label)} value={s.value} color={T.accent} size={26}/>
          </Card>
        ))}
      </Grid>
      <Card pad={0} style={{overflow:'hidden'}}>
        <div style={{padding:'14px 18px',borderBottom:`1px solid ${BD}`}}>
          <SectionHeader title={t('Tasarımlarım')} right={<Inp2 value={search} onChange={setSearch} placeholder={t('Ara...')} style={{width:200}}/>}/>
        </div>
        {!loading&&feeds.length>0&&(
          <div style={{display:'grid',gridTemplateColumns:'2fr 1.5fr 1fr 1fr 1.4fr',padding:'10px 18px',borderBottom:`1px solid ${BD}`,fontSize:10,color:MT,textTransform:'uppercase' as const,letterSpacing:'0.1em'}}>
            {['Tasarım Adı','Katalog','Ürün','Durum','İşlemler'].map(h=><span key={h}>{t(h)}</span>)}
          </div>
        )}
        {loading?<PoyaisSpinner size={36} pad={24}/>
        :filtered.length===0?(
          <div style={{padding:'60px 18px',textAlign:'center' as const}}>
            <p style={{margin:'0 0 6px',fontSize:15,fontWeight:700,color:TX}}>{t('Henüz hiç tasarım yok')}</p>
            <p style={{margin:'0 0 20px',fontSize:13,color:MT}}>{hasFeed?t('İlk dinamik feed tasarımınızı oluşturun'):t('Önce Ayarlar sekmesinden bir feed bağlayın.')}</p>
            <Button variant="solid" color={T.accent} onClick={openWizard}>{hasFeed?t('Yeni Tasarım'):t('Ayarlar’a Git')}</Button>
          </div>
        ):filtered.map((feed,i)=>(
          <div key={feed.id} style={{display:'grid',gridTemplateColumns:'2fr 1.5fr 1fr 1fr 1.4fr',padding:'14px 18px',borderBottom:i<filtered.length-1?`1px solid ${S3}`:'none',alignItems:'center',transition:'background 0.1s'}}
            onMouseEnter={e=>(e.currentTarget.style.background=S2)} onMouseLeave={e=>(e.currentTarget.style.background='transparent')}>
            <div>
              <div style={{fontSize:13,color:TX,fontWeight:600,marginBottom:2}}>{feed.name}</div>
              <div style={{fontSize:10,color:MT}}>{feed.createdAt} · 🖼 {t('Görsel')}</div>
            </div>
            <span style={{fontSize:12,color:MT}}>{feed.catalog}</span>
            <span style={{fontSize:13,color:TX,fontWeight:600}}>{(feed.cached||feed.products||0)>0?(feed.cached||feed.products).toLocaleString('tr'):'—'}</span>
            <StatusBadge status={feed.status}/>
            <div style={{display:'flex',gap:6}}>
              <Btn small onClick={()=>setEditorFeed(feed)}>{t('Düzenle')}</Btn>
              <button onClick={()=>{const u=feed.proxy_url||`${window.location.origin}${API}/proxy?feed_id=${feed.id}`;navigator.clipboard.writeText(u).catch(()=>{});setCopied(feed.id);setTimeout(()=>setCopied(null),2000);}}
                style={{padding:'5px 10px',borderRadius:6,fontSize:11,fontWeight:700,cursor:'pointer',background:copied===feed.id?'rgba(76,175,125,0.1)':'transparent',border:`1px solid ${copied===feed.id?GR:BD}`,color:copied===feed.id?GR:MT,transition:'all 0.15s'}}>
                {copied===feed.id?t('✓ Kopyalandı'):'🔗 URL'}
              </button>
              <button onClick={async()=>{
                if(!window.confirm(t('Bu tasarımı silmek istediğinize emin misiniz?'))) return;
                const tok=localStorage.getItem('poyais_token');
                await fetch(`${API}/designs/${feed.id}`,{method:'DELETE',headers:{'Authorization':`Bearer ${tok}`}}).catch(()=>{});
                setFeeds(prev=>prev.filter(f=>f.id!==feed.id));
              }} style={{padding:'5px 10px',borderRadius:6,fontSize:11,fontWeight:700,cursor:'pointer',background:'rgba(224,82,82,0.08)',border:`1px solid ${RD}`,color:RD,transition:'all 0.15s'}}>
                {t('Sil')}
              </button>
            </div>
          </div>
        ))}
      </Card>
      </>)}
      {wizardOpen&&<CreateWizardModal onClose={()=>setWizardOpen(false)} catalogs={catalogs} feedsList={feedRegistry} previewProduct={previewProduct} onOpenEditor={(f:Feed)=>{setFeeds(p=>{const e=p.find(x=>x.id===f.id);return e?p:[f,...p];});setEditorFeed(f);}}/>}
    </div>
  );
}
